// @ts-nocheck
import moment, { Moment } from "moment";
import store from "../store";
import { getFlightClient } from "./flight-client";

import "../vendor/libs/react-toastify/react-toastify.scss";
import axios, { Method, ResponseType } from "axios";
import { GuidEmpty, showToastMessages, showToastSuccessAndErrorMessagesFromApiResponse } from "../utils";
import { BookingPriceInfo, LatLng } from "./types/common";
import { BookingDotComConfig } from "./types/bookingDotComTypes";

const environments = {
  live: {
    ExternalApiUrl: "https://external.limolink.net",
    AccountApiUrl: "https://entities-api.limolink.net",
    CbsApiUrl: "https://api.limolink.net",
    GoldsainteApiUrl: "https://gs.limolink.net",
    CbsOldApiUrl: "https://account.chauffeurbookingsoftware.com",
    EntitiesApiUrl: "https://entities-api.limolink.net",
    PayPalCustomPaymentMethodId: "cpmt_1S8CgXGPWN0YPKfyftroV1KM",
    ZaloPayCustomPaymentMethodId: "cpmt_1S8Y2cGPWN0YPKfyEqF8M4Ll",
    VietQRCustomPaymentMethodId: "cpmt_1S8YyqGPWN0YPKfy266XAV0i",
  },
  staging: {
    ExternalApiUrl: "https://test-external.limolink.net",
    AccountApiUrl: "https://test-entities-api.limolink.net",
    CbsApiUrl: "https://test-cbs-api.limolink.net",
    GoldsainteApiUrl: "https://gs-test.limolink.net",
    CbsOldApiUrl: "https://ta.chauffeurbookingsoftware.com",
    EntitiesApiUrl: "https://test-entities-api.limolink.net", //"https://test-entities-api.limolink.net https://localhost:7028"
    PayPalCustomPaymentMethodId: "cpmt_1S8D3TGPWN0YPKfyuipvxofm",
    ZaloPayCustomPaymentMethodId: "cpmt_1S8CoeGPWN0YPKfypVkHBAm4",
    VietQRCustomPaymentMethodId: "cpmt_1S8YzmGPWN0YPKfySGj0xqNq",
  },
  local: {
    ExternalApiUrl: "https://test-external.limolink.net", //"https://localhost:44308",
    AccountApiUrl: "https://localhost:7028",
    CbsApiUrl: "https://localhost:44331",
    GoldsainteApiUrl: "https://gs-test.limolink.net", //"https://localhost:44313",
    CbsOldApiUrl: "https://account.chauffeurbookingsoftware.com",
    EntitiesApiUrl: "https://localhost:7028",
    PayPalCustomPaymentMethodId: "cpmt_1S8D3TGPWN0YPKfyuipvxofm",
    ZaloPayCustomPaymentMethodId: "cpmt_1S8CoeGPWN0YPKfypVkHBAm4",
    VietQRCustomPaymentMethodId: "cpmt_1S8YzmGPWN0YPKfySGj0xqNq",
  },
};

const environment = environments.live;
export const EntitiesApiUrl = process.env.REACT_APP_VERCEL_API_EntitiesApiUrl || environment.EntitiesApiUrl;
export const CbsOldApiUrl = process.env.REACT_APP_VERCEL_API_CbsOldApiUrl || environment.CbsOldApiUrl;
export const AccountApiUrl = process.env.REACT_APP_VERCEL_API_AccountApiUrl || environment.AccountApiUrl;
export const ExternalApiUrl = process.env.REACT_APP_VERCEL_API_ExternalApiUrl || environment.ExternalApiUrl;

// Stripe Custom Payment Method IDs (environment-based)
export const PayPalCustomPaymentMethodId = environment.PayPalCustomPaymentMethodId;
export const ZaloPayCustomPaymentMethodId = environment.ZaloPayCustomPaymentMethodId;
export const VietQRCustomPaymentMethodId = environment.VietQRCustomPaymentMethodId;

export const CbsApiUrl = (useRegion: boolean = true) => {
  // const state = store.getState();
  // const regionApiUrl = state.user?.apis?.cbsApi?.[state.user?.preferredRegion];

  // if (environment === environments.live && state.user?.preferredRegion !== "main" && regionApiUrl) {
  //   return regionApiUrl;
  // }

  return process.env.REACT_APP_VERCEL_API_CbsApiUrl || environment.CbsApiUrl;
};

export const GoldsainteApiUrl = process.env?.REACT_APP_VERCEL_API_GoldsainteApiUrl
  ? process.env?.REACT_APP_VERCEL_API_GoldsainteApiUrl
  : environment.GoldsainteApiUrl;

// Token refresh lock: ensures only one refresh request is in-flight at a time.
// When multiple requests fail with 401 simultaneously, they all await the same promise.
let refreshTokenPromise: Promise<LoginResult | null> | null = null;

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    console.error("Interceptor request error:", error);
    const { config: originalRequest, response } = error;

    if (!response) {
      return Promise.reject(error);
    }

    const shouldRetry = (response.status === 401 || response.status === 301 || originalRequest.method === "get") && !originalRequest._retry;

    if (shouldRetry) {
      const state = store.getState();
      console.log("Interceptor retrying request for URL:", originalRequest?.url);
      console.log("Interceptor status:", response.status, "hasRefreshToken:", !!state.user?.refreshToken);

      // If a refresh is already in progress, reuse its promise instead of making a new call
      if (!refreshTokenPromise) {
        refreshTokenPromise = refreshUserToken(state.user?.email, state.user?.refreshToken).finally(() => {
          refreshTokenPromise = null;
        });
      }

      const userProfile = await refreshTokenPromise;

      if (userProfile) {
        // refreshUserToken already updates Redux store with latest user preferences
        originalRequest.headers["Authorization"] = `Bearer ${userProfile.jwtToken}`;
        originalRequest._retry = true;
        return axios(originalRequest);
      } else {
        console.error("Interceptor logout due to invalid token for URL:", originalRequest?.url);
        await store.dispatch({ type: "setToken", data: null });
      }
    }

    return Promise.reject(error);
  }
);

function showError(message: string) {
  showToastMessages(message);
}

//#region User management API
export async function getBookingToken(): Promise<ApiResponse<LoginResult> | null> {
  const url = `${AccountApiUrl}/api/Account/bookingToken`;
  const config = getPostOptions(null);

  try {
    const timestamp = new Date().getTime();
    const urlWithTimestamp = url.includes("?") ? `${url}&t=${timestamp}` : `${url}?t=${timestamp}`;
    const response = await axios.post(urlWithTimestamp, null, config);
    const result = response.data;
    if (result.success) {
      return result as ApiResponse<LoginResult>;
    } else {
      showError("An error occurred while getting booking token. Please try again.");
      return null;
    }
  } catch (error) {
    console.error("Get booking token error:", error);
    showError("An error occurred while getting booking token. Please try again.");
    return null;
  }
}

/**
 * Closes the account belonging to the supplied token.
 *
 * Takes the JWT explicitly rather than reading it from the store, because the only caller is
 * the public delete-account page: the visitor authenticates there to prove the account is
 * theirs, but is never signed into the admin session, so `getPostOptions` would send no
 * Authorization header at all.
 *
 * The account is closed, not erased - sign-in is refused and sessions are revoked, and the
 * office is emailed so a person can carry out the erasure and reverse a mistake.
 */
export async function closeMyAccountWithToken(jwtToken: string): Promise<boolean> {
  try {
    const url = `${AccountApiUrl}/accountApi/closeMyAccount`;
    const response = await axios.post(
      url,
      { source: "web" },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
      },
    );
    return response?.data?.success === true;
  } catch (error) {
    console.error("closeMyAccount error:", error);
    return false;
  }
}

export async function loginUser(email: string, password: string): Promise<ApiResponse<LoginResult> | null> {
  const body = { email, password };
  const config = getPostOptions(body);

  try {
    const url = `${AccountApiUrl}/accountApi/weblogin`;
    const timestamp = new Date().getTime();
    const urlWithTimestamp = url.includes("?") ? `${url}&t=${timestamp}` : `${url}?t=${timestamp}`;
    const response = await axios.post(urlWithTimestamp, body, config);
    const result = response.data;

    if (result.success) {
      return result as ApiResponse<LoginResult>;
    } else {
      showError("Invalid email or password. Please try again.");
      return null;
    }
  } catch (error) {
    console.error("Login error:", error);
    showError("An error occurred at login. Please try again.");
    return null;
  }
}

/**
 * DEV-ONLY. Logs in as any user by email, with no password, via the DEBUG-gated
 * endpoint in Core.API (AccountController.DebugLoginAs).
 *
 * Guarded here so it cannot be called from a production bundle, and guarded again
 * server-side (Debug build + Development environment + config flag + loopback only).
 * The server returns the same payload as a real weblogin, so the caller treats the
 * result identically.
 */
export async function debugLoginAs(email: string): Promise<ApiResponse<LoginResult> | null> {
  if (process.env.NODE_ENV !== "development") return null;

  try {
    const url = `${AccountApiUrl}/accountApi/debug/loginas?email=${encodeURIComponent(email)}&t=${new Date().getTime()}`;
    const response = await axios.post(url, {}, getPostOptions({}));
    const result = response.data;

    if (result?.success) {
      return result as ApiResponse<LoginResult>;
    }
    showError(result?.message || `Dev login failed for ${email}.`);
    return null;
  } catch (error) {
    // A 404 here is the expected answer when any server-side gate is closed.
    console.error("Dev login error:", error);
    showError(`Dev login failed for ${email}. Is Core.API running in Debug on localhost?`);
    return null;
  }
}

export async function requestResetPassword(request: any): Promise<ApiResponse> {
  const url = `${AccountApiUrl}/api/Account/reset-password-email?email=${encodeURIComponent(request.email)}`;
  return post(url, request);
}
export async function getGovReport(request: any): Promise<object> {
  const url = `${EntitiesApiUrl}/api/baocao/onlinegov`;
  return post(url, request);
}
export async function resetPasswordAsAdmin(request: { userId: string; newPassword: string }): Promise<ApiResponse<LoginResult>> {
  const url = `${AccountApiUrl}/api/account/reset-password-as-admin`;
  return post(url, request);
}

export async function resetPassword(request: {
  email: string;
  newPassword: string;
  token: string;
  isMobile: false;
}): Promise<ApiResponse<LoginResult>> {
  const url = `${AccountApiUrl}/api/Account/reset-password`;
  return post(url, request);
}

export async function getUserInfo(): Promise<LoginResult | null> {
  const config = getPostOptions(null);

  try {
    //const response = await post(`${CbsOldApiUrl}/accountApi/tryGetUserInfo`, null, config);
    const response = await post(`${AccountApiUrl}/accountApi/tryGetUserInfo`, null, config);

    if (response.success) {
      return response.result as LoginResult;
    } else {
      showError("An error occurred while loading your user profile. Please try again.");
      return null;
    }
  } catch (error) {
    console.error("Get user info error:", error);
    return null;
  }
}

// New function for impersonation
export async function impersonateUser(targetUserEmail: string): Promise<ApiResponse<LoginResult> | null> {
  const body = { targetUserEmail };
  // Assuming AccountApiUrl is the correct base URL for this endpoint
  const url = `${AccountApiUrl}/api/Account/impersonate`;
  return post(url, body);
}

// Account-level API credentials (client_id / client_secret) for the signed-in user's account.
// See Account-API-Credentials-Admin-UI.md — the accountGuid/websiteGuid are derived server-side from the JWT.
export interface AccountApiCredentialStatus {
  accountGuid: string;
  defaultWebsiteGuid: string;
  clientId: string | null;
  enabled: boolean;
  createdAtUtc: string | null;
  revokedAtUtc: string | null;
}

export interface AccountApiCredentialProvision {
  accountGuid: string;
  defaultWebsiteGuid: string;
  clientId: string;
  clientSecret: string;
  createdAtUtc: string;
  secretShownOnce: boolean;
}

export interface AccountApiCredentialRevocation {
  accountGuid: string;
  clientId: string | null;
  revoked: boolean;
  revokedAtUtc: string;
}

export async function getAccountApiCredentialStatus(): Promise<ApiResponse<AccountApiCredentialStatus> | null> {
  const url = `${AccountApiUrl}/api/Account/apiCredentials`;
  return getResponse(url);
}

// First call generates credentials; every later call rotates the secret (client_id stays stable).
export async function provisionAccountApiCredentials(): Promise<ApiResponse<AccountApiCredentialProvision> | null> {
  const url = `${AccountApiUrl}/api/Account/apiCredentials/provision`;
  return post(url, {});
}

export async function revokeAccountApiCredentials(): Promise<ApiResponse<AccountApiCredentialRevocation> | null> {
  const url = `${AccountApiUrl}/api/Account/apiCredentials/revoke`;
  return post(url, {});
}

// Function to get website administrators
export async function getWebsiteAdministrators(websiteId: string): Promise<ApiResponse<WebsiteAdministrator[]> | null> {
  const url = `${AccountApiUrl}/api/Account/website/${websiteId}/admins`;
  return getResponse(url);
}
// Function to get website staff (Administrator, Dispatcher, Support Operator)
export async function getWebsiteStaffUsers(websiteId: string): Promise<ApiResponse<WebsiteAdministrator[]> | null> {
  const url = `${AccountApiUrl}/api/Account/website/${websiteId}/staff`;
  return getResponse(url);
}
// Function to get accounts of website
export async function getAccountsOfWebsite(websiteId: string): Promise<ApiResponse<WebsiteAdministrator[]> | null> {
  const url = `${AccountApiUrl}/api/Account/website/${websiteId}/accounts`;
  return getResponse(url);
}
// Function to get accounts of website
export async function getAccountDetailBySuperAdmin(userId: string): Promise<ApiResponse<any> | null> {
  const url = `${AccountApiUrl}/api/Account/${userId}/super-admin`;
  return getResponse(url);
}
// Function to remove admin role from user
export async function removeAdminRole(userId: string, websiteId: string): Promise<ApiResponse<any> | null> {
  const role = "Administrator";
  const url = `${AccountApiUrl}/api/Account/${userId}/website/${websiteId}/role/${role}/remove`;
  return post(url, {});
}

// Valid roles for account role management
export const VALID_ROLES = [
  "Administrator",
  "Driver",
  "Client",
  "CarOwner",
  "Dispatcher",
  "Data Entry",
  "Support Operator",
  "SystemAdmin",
  "Agent",
  "Fleet Manager",
  "Airport Greeter",
  "Pricing Manager",
  "Credential Manager",
] as const;

// Roles managed specifically by AccountsWebsiteCard.
// Administrator, Driver, Client, SystemAdmin and CarOwner have dedicated screens.
export const ACCOUNT_CARD_MANAGED_ROLES = [
  "Dispatcher",
  "Data Entry",
  "Support Operator",
  "Fleet Manager",
  "Airport Greeter",
  "Pricing Manager",
  "Agent",
  "Credential Manager",
] as const;

// Function to update account roles for a website
export async function updateAccountRoles(
  userId: string,
  websiteId: string,
  roles: string[],
  allowedRegionConfigs?: AllowedRegionConfig[],
  clientAccess?: ClientAccess
): Promise<ApiResponse<any> | null> {
  const url = `${AccountApiUrl}/api/Account/${userId}/website/${websiteId}/roles`;
  // clientAccess undefined = leave the user's current client access unchanged
  return put(url, { roles, allowedRegionConfigs, clientAccess });
}
// Function to remove role from user
// export async function removeRole(userId: string, websiteId: string, role: string): Promise<ApiResponse<any> | null> {
//   const url = `${AccountApiUrl}/api/Account/${userId}/website/${websiteId}/role/${role}/remove`;
//   return post(url, {});
// }
// Function to reset admin password
export async function resetAdminPassword(userId: string): Promise<ApiResponse<any> | null> {
  const url = `${AccountApiUrl}/api/Account/users/${userId}/reset-password`;
  return post(url, {});
}

// New function to reset admin password by email and websiteId
export async function resetPasswordByEmail(email: string): Promise<ApiResponse<any> | null> {
  const url = `${AccountApiUrl}/api/Account/reset-password-email?email=${encodeURIComponent(email)}`;
  return post(url, {});
}

// Function to add admin role to user by email
// This first looks up a user by email, and then grants them the Administrator role
export async function addAdminRole(email: string, websiteId: string): Promise<ApiResponse<any> | null> {
  try {
    // First step: Find user by email to get the userId
    const findUserUrl = `${AccountApiUrl}/api/Account/users/find?email=${encodeURIComponent(email)}`;
    let userId: string;
    try {
      // Use getResponse so we receive the full ApiResponse (success, result, etc.)
      const userResponse = await getResponse(findUserUrl);
      if (!userResponse || !userResponse.success || !userResponse.result) {
        return {
          success: false,
          message: `User with email ${email} not found.`,
          statusCode: 404,
          result: null,
          errors: null,
        } as ApiResponse<any>;
      }
      userId = userResponse.result.userId;
    } catch (findError: any) {
      // Axios throws for non-2xx; map HTTP 404 → statusCode 404 so the UI can show create-user flow
      if (findError?.response?.status === 404) {
        return {
          success: false,
          message: `User with email ${email} not found.`,
          statusCode: 404,
          result: null,
          errors: null,
        } as ApiResponse<any>;
      }
      throw findError;
    }

    // Second step: Grant Administrator role to the found user
    const role = "Administrator";
    const url = `${AccountApiUrl}/api/Account/${userId}/website/${websiteId}/role/${role}/grant`;
    return post(url, {});
  } catch (error) {
    console.error("Error adding admin role:", error);
    return {
      success: false,
      message: "An error occurred while adding admin role.",
      statusCode: 500,
      result: null,
      errors: error instanceof Error ? [error.message] : ["Unknown error"],
    } as ApiResponse<any>;
  }
}

export interface RegisterModel {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
}
// Function to add admin
export async function addNewAdmin(websiteId: string, userInfo: RegisterModel): Promise<ApiResponse<any> | null> {
  const url = `${AccountApiUrl}/api/Account/${websiteId}/new-admin`;
  return post(url, userInfo);
}

export interface AddNewAccountModel extends RegisterModel {
  role: string;
}
// Create a new user on the website with a specific role
export async function addNewAccount(websiteId: string, userInfo: AddNewAccountModel): Promise<ApiResponse<any> | null> {
  const url = `${AccountApiUrl}/api/Account/${websiteId}/new-account`;
  return post(url, userInfo);
}

// Find a user by email — returns the full ApiResponse (userId, email, firstName, lastName)
export async function findUserByEmail(email: string): Promise<ApiResponse<any> | null> {
  const url = `${AccountApiUrl}/api/Account/users/find?email=${encodeURIComponent(email)}`;
  try {
    return await getResponse(url);
  } catch (err: any) {
    if (err?.response?.status === 404) {
      return { success: false, statusCode: 404, result: null, data: null, errors: [], errorMessages: [], successMessages: [] };
    }
    throw err;
  }
}
// Function to grant admin role to user by userId directly
export async function grantAdminRole(userId: string, websiteId: string): Promise<ApiResponse<any> | null> {
  const role = "Administrator";
  const url = `${AccountApiUrl}/api/Account/${userId}/website/${websiteId}/role/${role}/grant`;
  return post(url, {});
}
// Function to grant admin role to user by userId directly
// export async function grantRole(userId: string, websiteId: string, role: string): Promise<ApiResponse<any> | null> {
//   const url = `${AccountApiUrl}/api/Account/${userId}/website/${websiteId}/role/${role}/grant`;
//   return post(url, {});
// }
export type UpdateUserInfoRequest = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  guid: string;
  dateFormat: string;
  timeFormat: string;
  preferredRegion: string;
  signatureImageUrl?: string;
};

export function updateUserInfo(updateUserInfoRequest: UpdateUserInfoRequest) {
  const url = `${EntitiesApiUrl}/accountApi/updateAccountInfo`;
  return post(url, updateUserInfoRequest);
}

export type ResetUserPasswordRequest = {
  websiteGuid: string;
  clientGuid: string;
  password: string;
  confirmPassword: string;
};

export function resetUserPassword(updateUserInfoRequest: ResetUserPasswordRequest): Promise<SimpleApiResponse> {
  const url = `${AccountApiUrl}/api/Account/resetClientPassword`;
  return post(url, updateUserInfoRequest);
}

export type ResetMyPasswordRequest = {
  password: string;
  confirmPassword: string;
  currentPassword: string;
};
export function resetMyPassword(updateUserInfoRequest: ResetMyPasswordRequest): Promise<SimpleApiResponse> {
  const url = `${CbsOldApiUrl}/accountApi/resetMyPassword`;
  return post(url, updateUserInfoRequest);
}
export function resetMyPassword_New(updateUserInfoRequest: ResetMyPasswordRequest): Promise<SimpleApiResponse> {
  const url = `${EntitiesApiUrl}/accountApi/resetMyPassword`;
  return post(url, updateUserInfoRequest);
}
export type ResetDriverPasswordRequest = {
  driverGuid: string;
  password: string;
  confirmPassword: string;
};

export function resetDriverPassword(request: ResetDriverPasswordRequest): Promise<ApiResponse> {
  const url = `${AccountApiUrl}/api/Account/resetDriverPassword`;
  return post(url, request);
}

export type ResetDriverPasswordByAdminRequest = {
  driverGuid: string;
};

export function resetDriverPasswordByAdmin(request: ResetDriverPasswordByAdminRequest): Promise<SimpleApiResponse> {
  const url = `${AccountApiUrl}/api/Account/resetDriverPassword`;
  return post(url, request);
}

export function refreshUserToken(email: string, refreshToken: string): Promise<LoginResult | null> {
  const payload = {
    email: email,
    refreshToken: refreshToken,
  };
  const requestOptions = getPostOptions(JSON.stringify(payload));
  return fetch(`${AccountApiUrl}/accountApi/refreshToken`, requestOptions)
    .then((response) => response.json())
    .then((result) => {
      if (result.success) {
        const userProfile = result.result as LoginResult;
        // Always update Redux store with latest user preferences from backend
        store.dispatch({ type: 'setUserInfo', data: userProfile });
        return userProfile;
      } else {
        showError("An error occurred while loading your user profile. Please try again.");
      }
      return null;
    })
    .catch((error) => {
      console.log("error", error);
      return null;
    });
}

export function setDefaultWebsite(websiteGuid: string) {
  const requestOptions = getPostOptions(null);
  return fetch(`${EntitiesApiUrl}/accountApi/setDefaultWebsite?websiteGuid=${websiteGuid}`, requestOptions)
    .then((response) => response.json())
    .then((result) => {
      console.log(result);
      return true;
    })
    .catch((error) => {
      console.log("error", error);
      showError("There was an error when changing the default website. Please try again.");
      return false;
    });
}

//#endregion User management API

export function getIntervalStats(
  startDate: Date,
  endDate: Date,
  interval: number,
  websiteGuid: string = store.getState().user.defaultWebsiteGuid
): Promise<IntervalStatsInfo | null> {
  const requestOptions = getGetOptions();
  const start = encodeURI(moment(startDate).toISOString());
  const end = encodeURI(moment(endDate).toISOString());
  const url = `${ExternalApiUrl}/BookingDotCom/get-stats?WebsiteGuid=${websiteGuid}&FromDate=${start}&ToDate=${end}&IntervalInMinutes=${interval}`;

  return fetch(url, requestOptions)
    .then((response) => response.json())
    .then((result) => {
      console.log(result);
      return result as IntervalStatsInfo;
    })
    .catch((error) => {
      console.log("error", error);
      showError("There was an error when retrieving booking.com requests. Please try again.");
      return null;
    });
}

export type GetSearchRoutesRequest = {
  regionCenter: LatLng;
  radiusInMeters: number;
  minimumCount: number;
};

export type GetSearchRoutesResponse = {
  source: string;
  fromPlaceId: string | null;
  fromName: string;
  fromAddress: string;
  fromCity: string;
  fromCountry: string;
  fromPostalCode: string;
  fromIata: string | null;
  ianaTimezone: string;
  from: LatLng;
  // Add other fields here if needed
};
export function getSearchRequests(request: GetSearchRoutesRequest): Promise<WebApiResponse<GetSearchRoutesResponse>> {
  const url = `${ExternalApiUrl}/BookingDotCom/getRoutes-test`;
  return post(url, request);
}

export function BookingDotComRequests() { }

export function getBookingDotComRequestsDownloadUrl(
  websiteGuid: string,
  startDate: Date,
  endDate: Date,
  maxCount: number = 900000,
  source: string = "api"
) {
  const start = encodeURI(moment(startDate).toISOString());
  const end = encodeURI(moment(endDate).toISOString());
  if (source === "api") {
    return `${ExternalApiUrl}/BookingDotCom/get-requests?WebsiteGuid=${websiteGuid}&FromDate=${start}&ToDate=${end}&NumberOfItems=${maxCount}`;
  } else {
    return `${ExternalApiUrl}/BookingDotCom/get-requests-excel?WebsiteGuid=${websiteGuid}&FromDate=${start}&ToDate=${end}&NumberOfItems=${maxCount}`;
  }
}

export function getBookingDotComRequests(websiteGuid: string, startDate: Date, endDate: Date): Promise<any> {
  const start = encodeURI(moment(startDate).toISOString());
  const end = encodeURI(moment(endDate).toISOString());
  const url = `${EntitiesApiUrl}/api/Bookingdotcom/requests?WebsiteGuid=${websiteGuid}&FromDate=${start}&ToDate=${end}&NumberOfItems=900000`;
  return getResponse(url)
    .then((result) => {
      return result.data;
    })
    .catch((error) => {
      console.log("getBookingDotComRequests error:", error);
      showError("There was an error when retrieving booking.com requests. Please try again.");
      return null;
    });
}

export type GroupedRoutePrice = {
  transportCategory: string;
  price: { salePriceMin: number; salePriceMax?: number; currency: string };
  description?: string | null;
  geniusDiscount?: { percentageDiscount: number } | null;
  pricePlanGuid?: string;
  fixedPriceGuid?: string;
};

export type GroupedRouteLatLng = { lat: number; lng: number };

export type GroupedRouteItem = {
  from: { address: string; latLng?: GroupedRouteLatLng | null };
  to: { address: string; latLng?: GroupedRouteLatLng | null };
  region?: string | null;
  requestCount: number;
  pricedCount: number;
  unpricedCount: number;
  firstSeen: string;
  lastSeen: string;
  distanceInMeters: number;
  lastPickupDateTimeUtc: string;
  version?: string | null;
  geniusInfo?: { level: string } | null;
  lastResult: GroupedRoutePrice[];
  appliedSurcharges?: string[] | null;
  noPriceReason?: string | null;
  lastLeadTimeMinutes?: number | null;
};

export type GroupedRoutesQuery = {
  regionName?: string | null;
  minRequestCount?: number;
  onlyNoPrice?: boolean;
  take?: number;
};

/**
 * Groups booking.com requests by from/to over a short recent window so dispatchers can spot
 * in-demand routes for which we do not offer prices. All aggregation happens server-side.
 */
export function getBookingDotComGroupedRoutes(
  websiteGuid: string,
  startDate: Date,
  endDate: Date,
  query: GroupedRoutesQuery = {}
): Promise<GroupedRouteItem[]> {
  const params = new URLSearchParams({
    WebsiteGuid: websiteGuid,
    FromDate: moment(startDate).toISOString(),
    ToDate: moment(endDate).toISOString(),
    MinRequestCount: String(query.minRequestCount ?? 1),
    OnlyNoPrice: String(query.onlyNoPrice ?? false),
    Take: String(query.take ?? 300),
  });
  if (query.regionName) {
    params.set("RegionName", query.regionName);
  }
  const url = `${EntitiesApiUrl}/api/Bookingdotcom/requests/grouped-routes?${params.toString()}`;
  return getResponse(url)
    .then((result) => (result?.data ?? []) as GroupedRouteItem[])
    .catch((error) => {
      console.log("getBookingDotComGroupedRoutes error:", error);
      showError("There was an error when retrieving booking.com routes. Please try again.");
      return [] as GroupedRouteItem[];
    });
}

export type RouteRequestLogItem = {
  id: string;
  createdAt: string;
  pickupDateTimeUtc: string;
  pickupTimezone?: string | null;
  leadTimeMinutes?: number | null;
  priced: boolean;
  noPriceReason?: string | null;
  appliedSurcharges?: string[] | null;
  geniusInfo?: { level: string } | null;
  version?: string | null;
  status?: string | null;
  errorMessage?: string | null;
  result: GroupedRoutePrice[];
};

/**
 * Individual booking.com requests for a single route (exact from/to match) within a window, newest
 * first — used by the per-route "View Logs" drill-down.
 */
export function getBookingDotComRouteLogs(
  websiteGuid: string,
  fromAddress: string,
  toAddress: string,
  startDate: Date,
  endDate: Date,
  take: number = 100
): Promise<RouteRequestLogItem[]> {
  const params = new URLSearchParams({
    WebsiteGuid: websiteGuid,
    FromAddress: fromAddress,
    ToAddress: toAddress,
    FromDate: moment(startDate).toISOString(),
    ToDate: moment(endDate).toISOString(),
    Take: String(take),
  });
  const url = `${EntitiesApiUrl}/api/Bookingdotcom/requests/route-logs?${params.toString()}`;
  return getResponse(url)
    .then((result) => (result?.data ?? []) as RouteRequestLogItem[])
    .catch((error) => {
      console.log("getBookingDotComRouteLogs error:", error);
      showError("There was an error when retrieving booking.com route logs. Please try again.");
      return [] as RouteRequestLogItem[];
    });
}

/** Fetches the full raw booking.com request document (Mongo extended JSON) for inspection. */
export function getBookingDotComRequestRaw(websiteGuid: string, id: string): Promise<string | null> {
  const url = `${EntitiesApiUrl}/api/Bookingdotcom/requests/${id}/raw?websiteGuid=${websiteGuid}`;
  return getResponse(url)
    .then((result) => (result?.success ? (result.data as string) : null))
    .catch((error) => {
      console.log("getBookingDotComRequestRaw error:", error);
      showError("There was an error when retrieving the request document. Please try again.");
      return null;
    });
}

//#region Unified route-demand report (booking widget + booking.com, one contract)

/**
 * Which price-request log to read. Ctrip is absent on purpose — it is a separate service with its own
 * store and its own semantics, and the screen calls it directly rather than through this API.
 */
export const ROUTE_REQUEST_SOURCES = {
  bookingScript: "booking-script",
  bookingCom: "bookingcom",
  admin: "admin",
  api: "api",
} as const;

export type RouteRequestSource = (typeof ROUTE_REQUEST_SOURCES)[keyof typeof ROUTE_REQUEST_SOURCES];

/**
 * Machine-readable reason a request returned nothing bookable, shared by every source: the pricing
 * engine reports it directly for widget traffic, and booking.com reasons are mapped onto the same
 * vocabulary server-side. Null when the request was quoted.
 */
export type NoPriceReasonCode =
  | "no-distance"
  | "no-duration"
  | "no-region"
  | "no-vehicles-enabled"
  | "no-price-configured"
  | "all-fully-booked"
  | "supplier-mapping-missing"
  | "engine-error";

/** How far a request got down the pricing pipeline, per vehicle. Booking-script sources only. */
export type PriceRequestDiagnostics = {
  vehiclesConsidered: number;
  vehiclesPriced: number;
  vehiclesWithoutPrice: number;
  vehiclesFullyBooked: number;
  vehiclesWithoutSupplierMapping: number;
  /** 0 means the matched plan is distance-priced only. */
  fixedRoutesInPlan: number;
  fixedRouteMatches: number;
  notes: string[];
};

/** One vehicle offered (or priced but unavailable), in source-neutral terms. */
export type RouteQuote = {
  vehicleName?: string | null;
  price: number;
  currency?: string | null;
  discountPercentage: number;
  isFullyBooked: boolean;
  computationDescription?: string | null;
};

export type BookingComRouteExtras = {
  version?: string | null;
  geniusLevel?: string | null;
  appliedSurcharges: string[];
};

export type BookingScriptRouteExtras = {
  currency?: string | null;
  /** 1 = one way, 2 = hourly, 3 = return. */
  bookingType: number;
  fixedPriceMatched: boolean;
  lastReferer?: string | null;
  diagnostics?: PriceRequestDiagnostics | null;
};

export type RouteDemandItem = {
  source: RouteRequestSource | string;
  from: { address: string; addressName?: string | null; latLng?: GroupedRouteLatLng | null };
  to: { address: string; addressName?: string | null; latLng?: GroupedRouteLatLng | null };
  region?: string | null;
  requestCount: number;
  pricedCount: number;
  unpricedCount: number;
  firstSeen: string;
  lastSeen: string;
  distanceInMeters: number;
  lastPickupDateTimeUtc: string;
  lastLeadTimeMinutes?: number | null;
  lastResult: RouteQuote[];
  noPriceReason?: string | null;
  noPriceReasonCode?: NoPriceReasonCode | string | null;
  bookingCom?: BookingComRouteExtras | null;
  bookingScript?: BookingScriptRouteExtras | null;
};

export type BookingComLogExtras = {
  geniusLevel?: string | null;
  appliedSurcharges: string[];
  version?: string | null;
  status?: string | null;
  errorMessage?: string | null;
};

export type BookingScriptLogExtras = {
  fixedPriceMatched: boolean;
  currency?: string | null;
  bookingType: number;
  isReturn: boolean;
  passengers: number;
  hasMeetAndGreet: boolean;
  promoCode?: string | null;
  distanceInKilometers: number;
  durationMs: number;
  referer?: string | null;
  diagnostics?: PriceRequestDiagnostics | null;
};

export type RouteLogItem = {
  id: string;
  source: RouteRequestSource | string;
  createdAt: string;
  pickupDateTimeUtc: string;
  pickupTimezone?: string | null;
  leadTimeMinutes?: number | null;
  priced: boolean;
  noPriceReason?: string | null;
  noPriceReasonCode?: NoPriceReasonCode | string | null;
  region?: string | null;
  result: RouteQuote[];
  bookingCom?: BookingComLogExtras | null;
  bookingScript?: BookingScriptLogExtras | null;
};

export type RouteDemandQuery = {
  regionName?: string | null;
  minRequestCount?: number;
  onlyNoPrice?: boolean;
  take?: number;
};

/**
 * Groups logged price requests by route over a window, so a dispatcher can see which routes customers
 * ask for and which of them we fail to quote. Same contract for every source.
 */
export function getRouteDemand(
  websiteGuid: string,
  source: RouteRequestSource | string,
  startDate: Date,
  endDate: Date,
  query: RouteDemandQuery = {}
): Promise<RouteDemandItem[]> {
  const params = new URLSearchParams({
    WebsiteGuid: websiteGuid,
    Source: source,
    FromDate: moment(startDate).toISOString(),
    ToDate: moment(endDate).toISOString(),
    MinRequestCount: String(query.minRequestCount ?? 1),
    OnlyNoPrice: String(query.onlyNoPrice ?? false),
    Take: String(query.take ?? 300),
  });
  if (query.regionName) {
    params.set("RegionName", query.regionName);
  }
  const url = `${EntitiesApiUrl}/api/PriceRequests/grouped-routes?${params.toString()}`;
  return getResponse(url)
    .then((result) => (result?.data ?? []) as RouteDemandItem[])
    .catch((error) => {
      console.log("getRouteDemand error:", error);
      showError("There was an error when retrieving route demand. Please try again.");
      return [] as RouteDemandItem[];
    });
}

/** Individual logged requests for one route (exact from/to match), newest first. */
export function getRouteRequestLogs(
  websiteGuid: string,
  source: RouteRequestSource | string,
  fromAddress: string,
  toAddress: string,
  startDate: Date,
  endDate: Date,
  take: number = 100
): Promise<RouteLogItem[]> {
  const params = new URLSearchParams({
    WebsiteGuid: websiteGuid,
    Source: source,
    FromAddress: fromAddress,
    ToAddress: toAddress,
    FromDate: moment(startDate).toISOString(),
    ToDate: moment(endDate).toISOString(),
    Take: String(take),
  });
  const url = `${EntitiesApiUrl}/api/PriceRequests/route-logs?${params.toString()}`;
  return getResponse(url)
    .then((result) => (result?.data ?? []) as RouteLogItem[])
    .catch((error) => {
      console.log("getRouteRequestLogs error:", error);
      showError("There was an error when retrieving route logs. Please try again.");
      return [] as RouteLogItem[];
    });
}

/** Full raw stored document for one logged request, for inspection. */
export function getRouteRequestRaw(
  websiteGuid: string,
  source: RouteRequestSource | string,
  id: string
): Promise<string | null> {
  const params = new URLSearchParams({ websiteGuid, source });
  const url = `${EntitiesApiUrl}/api/PriceRequests/${id}/raw?${params.toString()}`;
  return getResponse(url)
    .then((result) => (result?.success ? (result.data as string) : null))
    .catch((error) => {
      console.log("getRouteRequestRaw error:", error);
      showError("There was an error when retrieving the request document. Please try again.");
      return null;
    });
}

//#endregion

/** Downloads the booking.com request document as a text file. */
export function getBookingDotComRequestTextFile(id: string, fileName: string): Promise<Blob | null> {
  const params = new URLSearchParams();
  if (fileName) {
    params.set("fileName", fileName);
  }

  const query = params.toString();
  const url = `${EntitiesApiUrl}/api/Bookingdotcom/requests/${encodeURIComponent(id)}/txt${query ? `?${query}` : ""}`;
  return getBlobResponse(url)
    .then((result) => result as Blob)
    .catch((error) => {
      console.log("getBookingDotComRequestTextFile error:", error);
      showError("There was an error when downloading the request text file. Please try again.");
      return null;
    });
}

export function getWebsiteClients(websiteGuid: string): Promise<any> {
  return getResponse(`${CbsApiUrl()}/api/clients/getClients/${websiteGuid}`);
}

export function getCompaniesAndClients(websiteGuid: string): Promise<any> {
  return getResponse(`${CbsApiUrl()}/api/clients/getCompaniesAndClients/${websiteGuid}?includeAgents=true`);
}
export function getWebsiteClient(clientGuid: string): Promise<WebApiResponse<ClientInfo>> {
  return getResponse(`${CbsApiUrl()}/api/clients/${clientGuid}`);
}

export function getWebsiteDrivers(websiteGuid: string): Promise<DriverInfo[]> {
  return getResponse(`${CbsApiUrl()}/api/drivers/getWebsiteDrivers?websiteGuid=${websiteGuid}`);
}

export async function getWebsiteRegions(websiteGuid: string): Promise<RegionInfo[]> {
  try {
    // Call the same backend endpoint used by the price plans page, but directly
    // to avoid creating a circular dependency between ExternalApi and PricingAPI.
    const url = `${EntitiesApiUrl}/api/Prices/getAllPricePlans`;
    const response = await post(url, { websiteGuid });

    if (response?.success && response?.data?.regions) {
      // Parent regions are website-level plans only.
      const parentPlans = response.data.regions.filter(
        (plan: any) => !plan.clientGuid && !plan.supplierGuid && !plan.parentPriceGuid
      );

      return parentPlans.map((plan: any) => ({
        name: plan.name,
        guid: plan.pricePlanGuid,
      }));
    }

    return [] as RegionInfo[];
  } catch (error) {
    console.error("Error loading regions:", error);
    showError("An error occurred while loading regions. Please try again.");
    return [] as RegionInfo[];
  }
}

export function getProfitReportsLink(
  isApi: boolean,
  websiteGuid: string,
  startDate: Date,
  endDate: Date,
  clientId: string,
  selectedRegion: string,
  selectedDriverGuid: string,
  selectedSupplierGuid: string,
  includeCancelled: boolean,
  includeNoDriver: boolean,
  type: string
) {
  const start = encodeURI(moment(startDate).toISOString());
  const end = encodeURI(moment(endDate).toISOString());

  var url = isApi
    ? `${CbsApiUrl()}/api/Reports/BookingStats?WebsiteGuid=${websiteGuid}&FromDate=${start}&ToDate=${end}&Type=${type}`
    : `${CbsApiUrl()}/api/Reports/ExportToExcel?WebsiteGuid=${websiteGuid}&FromDate=${start}&ToDate=${end}&Type=${type}`;
  if (selectedDriverGuid) {
    url += `&driverguid=${selectedDriverGuid}`;
  }
  if (selectedSupplierGuid) {
    url += `&supplierGuid=${selectedSupplierGuid}`;
  }
  if (includeCancelled) {
    url += `&IncludeCancelled=${includeCancelled}`;
  }
  if (includeNoDriver) {
    url += `&IncludeNoDriver=${includeNoDriver}`;
  }
  if (clientId && clientId !== GuidEmpty) {
    url += `&ClientUserId=${clientId}`;
  }
  if (selectedRegion) {
    url += `&RegionGuid=${selectedRegion}`;
  }
  return url;
}

export function getProfitReports(
  websiteGuid: string,
  startDate: Date,
  endDate: Date,
  clientId: string,
  selectedRegion: string,
  selectedDriverGuid: string,
  selectedSupplierGuid: string,
  includeCancelled: boolean,
  includeNoDriver: boolean,
  type: string
): Promise<PerformanceInfo[] | null> {
  var requestOptions = {
    ...getGetOptions(),
    timeout: 240 * 1000, // Timeout in milliseconds
  };

  const url = getProfitReportsLink(
    true,
    websiteGuid,
    startDate,
    endDate,
    clientId,
    selectedRegion,
    selectedDriverGuid,
    selectedSupplierGuid,
    includeCancelled,
    includeNoDriver,
    type
  );

  return fetch(url, requestOptions)
    .then((response) => response.json())
    .then((result) => {
      //console.log(result);
      if (result.success) {
        return result.data as PerformanceInfo[];
      } else {
        showError("An error occurred while loading reports. Please try again.");
        return [] as PerformanceInfo[];
      }
    })
    .catch((error) => {
      //console.log('error', error);
      showError("An error occurred while loading reports. Please try again.");
      return [] as PerformanceInfo[];
    });
}

//#region Clients API

export function deleteClient(clientId: string) {
  const url = `${CbsApiUrl()}/api/Clients/deleteClient?clientId=${clientId}`;
  return post(url, null);
}

export function updateClient(client: Client) {
  const url = `${CbsApiUrl()}/api/Clients/updateClient`;
  return post(url, client);
}

export function registerTravelAgent(request: TravelAgentRegistrationRequest) {
  const url = `${CbsApiUrl()}/api/Clients/registerTravelAgent`;
  return post(url, request);
}

export function getTravelAgentRegistrationInfo(registrationGuid: string): Promise<WebApiResponse<{ email: string }>> {
  const url = `${CbsApiUrl()}/api/Clients/getTravelAgentRegistrationInfo/${registrationGuid}`;
  return getFromApi(url);
}

export function completeTravelAgentRegistration(request: CompleteTravelAgentRegistrationRequest): Promise<WebApiResponse<LoginResult>> {
  const url = `${CbsApiUrl()}/api/Clients/completeTravelAgentRegistration`;
  return post(url, request);
}

//#endregion Clients API

//#region Coupons API

export function getWebsiteCoupons(websiteGuid: string): Promise<Coupon[]> {
  return getResponse(`${CbsApiUrl()}/api/Coupon/getCoupons/${websiteGuid}`);
}

export function addCoupon(coupon: Coupon) {
  const url = `${CbsApiUrl()}/api/Coupon/addCoupon`;
  return post(url, coupon);
}
// 1c2c3bf1-39b3-480b-8136-44884351c2a6
// export function deleteCoupon(couponId: string) {
//   const url = `${CbsApiUrl()}/api/Coupons/deleteCoupon/${couponId}`;
//   return post(url, null);
// }

export function updateCoupon(client: Coupon) {
  const url = `${CbsApiUrl()}/api/Coupon/updateCoupon`;
  return post(url, client);
}

export function enableCoupon(websiteGuid: string, couponId: string, isEnabled: boolean) {
  const url = `${CbsApiUrl()}/api/Coupon/enableCoupon/${websiteGuid}/${couponId}?IsEnabled=${isEnabled}`;
  return post(url, null);
}

//#endregion Coupons API

//#region Suppliers API

/**
 * @deprecated Use getSuppliersOfWebsite() instead. This function uses the old CBS API
 * and returns SupplierInfo[] with complex coveredRegions structure.
 * getSuppliersOfWebsite() uses the newer Entities API and returns SupplierResponse[]
 * with simpler coveredRegions: string[] structure.
 */
export function getWebsiteSuppliers(websiteGuid: string): Promise<SupplierInfo[]> {
  //const requestOptions = getGetOptions();
  const url = `${CbsApiUrl()}/api/suppliers/getSuppliers?websiteGuid=${websiteGuid}`;
  return (
    getResponse(url) //fetch(`${CbsApiUrl()}/api/Suppliers/getSuppliers?websiteGuid=${websiteGuid}`, requestOptions)
      //.then(response => response.json())
      .then((result) => {
        //console.log(result);
        if (result.success) {
          return result.data as SupplierInfo[];
        } else {
          showError("An error occurred while loading suppliers. Please try again.");
          return [] as SupplierInfo[];
        }
      })
      .catch((error) => {
        //console.log('error', error);
        showError("An error occurred while loading suppliers. Please try again.");
        return [] as SupplierInfo[];
      })
  );
}

export interface SupplierResponse {
  supplierWebsiteGuid: string;
  name: string;
  companyName?: string;
  coveredRegions: string[];
  /** Currency the supplier is paid in */
  currency?: string | null;
}

export function getSuppliers(websiteGuid: string): Promise<SupplierResponse[]> {
  //const requestOptions = getGetOptions();
  const url = `${EntitiesApiUrl}/api/suppliers?websiteGuid=${websiteGuid}`;
  return (
    getResponse(url) //fetch(`${CbsApiUrl()}/api/Suppliers/getSuppliers?websiteGuid=${websiteGuid}`, requestOptions)
      //.then(response => response.json())
      .then((result) => {
        //console.log(result);
        if (result.success) {
          return result.data as SupplierResponse[];
        } else {
          showError("An error occurred while loading suppliers. Please try again.");
          return [] as SupplierResponse[];
        }
      })
      .catch((error) => {
        //console.log('error', error);
        showError("An error occurred while loading suppliers. Please try again.");
        return [] as SupplierResponse[];
      })
  );
}

export function getSuppliersOfWebsite(websiteGuid: string): Promise<SupplierResponse[]> {
  const url = `${EntitiesApiUrl}/api/suppliers?websiteGuid=${websiteGuid}`;
  return getResponse(url)
    .then((result) => {
      if (result.success) {
        return result.result as SupplierResponse[];
      } else {
        showError("An error occurred while loading suppliers. Please try again.");
        return [] as SupplierResponse[];
      }
    })
    .catch((error) => {
      showError("An error occurred while loading suppliers. Please try again.");
      return [] as SupplierResponse[];
    });
}

export function getWebsiteSupplierAndDriver(websiteGuid: string): Promise<SupplierInfo[]> {
  //const requestOptions = getGetOptions();
  const url = `${CbsApiUrl()}/api/bookings/GetSupplierAndDriver/${websiteGuid}`;
  return (
    getResponse(url) //fetch(`${CbsApiUrl()}/api/Suppliers/getSuppliers?websiteGuid=${websiteGuid}`, requestOptions)
      //.then(response => response.json())
      .then((result) => {
        //console.log(result);
        if (result.success) {
          return result.data.items as SupplierInfo[];
        } else {
          showError("An error occurred while loading suppliers. Please try again.");
          return [] as SupplierInfo[];
        }
      })
      .catch((error) => {
        //console.log('error', error);
        showError("An error occurred while loading suppliers. Please try again.");
        return [] as SupplierInfo[];
      })
  );
}

export function getPickupInstructions(pickupId: string, supplierDriverGuid: string, websiteGuid: string, airportIata?: string): Promise<any> {
  let url = `${EntitiesApiUrl}/api/pickup-instructions`;
  const params = new URLSearchParams();

  if (pickupId && pickupId.trim()) params.append("pickupId", pickupId);
  if (supplierDriverGuid && supplierDriverGuid.trim()) params.append("supplierDriverGuid", supplierDriverGuid);
  if (websiteGuid && websiteGuid.trim()) params.append("websiteGuid", websiteGuid);
  if (airportIata && airportIata.trim()) params.append("airportIata", airportIata);

  const qs = params.toString();
  if (qs) url += `?${qs}`;

  return getResponse(url);
}

// Convenience helper for the V2 pickup instructions page.
// Returns all pickup instruction entries for a website without applying supplier/driver or airport filters.
export function getAllPickupInstructionsForWebsite(websiteGuid: string): Promise<any> {
  return getPickupInstructions("", "", websiteGuid, "");
}

export function getPickupInstructionOwnersWithInstructions(websiteGuid: string): Promise<any> {
  const url = `${EntitiesApiUrl}/api/pickup-instructions/owners-with-instructions?websiteGuid=${websiteGuid}`;
  return getResponse(url);
}

export function deletePickupInstructions(supplierGuid: string): Promise<WebApiResponse<any>> {
  const url = `${EntitiesApiUrl}/api/pickup-instructions/supplier/${supplierGuid}`;
  return del(url);
}

export interface PickupImage {
  id?: string;
  imageUrl: string;
  description: string;
  altText: string;
}

export interface PickupMeetingPoint {
  name: string;
  location: string;
  pickupType?: string;
  instructionText: string;
  internalMarkdown?: string;
  pickupImages?: PickupImage[];
  videoUrls?: string[];
  sortOrder?: number;
}

export interface SavePickupInstructionsRequest {
  id: string;
  supplierDriverGuid: string | null;
  websiteGuid: string;
  name: string;
  airportIATA: string;
  pickupInstructions: string;
  pickupImages: PickupImage[];
}

// V2 extensions (kept optional for backward compatibility)
export type PickupInstructionsOwnerType = "Company" | "SupplierDriver" | "AirportDefault";

export type LocationBoundary = {
  name?: string;
  address?: string;
  center?: { lat: number; lng: number };
  type?: string;
  lat?: number;
  lng?: number;
  radiusInMeters?: number;
  points?: Array<{ lat: number; lng: number }>;
  polygonPoints?: Array<{ lat: number; lng: number }>;
  // allow extra server-provided props without breaking the UI
  [key: string]: any;
};

export type SavePickupInstructionsRequestV2 = SavePickupInstructionsRequest & {
  ownerType?: PickupInstructionsOwnerType;
  language?: string;
  pickupPointGroup?: string;
  pickupPointLabel?: string;
  instructionText?: string;
  meetingPoints?: PickupMeetingPoint[];
  steps?: string[];
  boundary?: LocationBoundary | null;
  meetingPointLocation?: string;
};

export function savePickupInstructions(
  request: Partial<SavePickupInstructionsRequestV2>
): Promise<WebApiResponse<PickupInstructionsItem[]>> {
  const url = `${EntitiesApiUrl}/api/pickup-instructions`;
  return post(url, request);
}

// Buffer Time API functions
export interface BufferTime {
  id?: string;
  websiteGuid: string;
  airportIATA: string;
  supplierDriverGuid?: string | null;
  isEnabled?: boolean;
  isRemoved?: boolean;
  updatedAt?: string;
  time: number; // in minutes
  description: string;
}

export interface SaveBufferTimeRequest {
  id?: string;
  websiteGuid: string;
  airportIATA: string;
  supplierDriverGuid?: string | null;
  time: number;
  description: string;
}

export function getBufferTime(id?: string, websiteGuid?: string, airportIATA?: string, supplierDriverGuid?: string): Promise<WebApiResponse<BufferTime[]>> {
  let url = `${EntitiesApiUrl}/api/buffer-time`;
  const params = new URLSearchParams();

  if (id) params.append("id", id);
  if (websiteGuid) params.append("websiteGuid", websiteGuid);
  if (airportIATA) params.append("airportIATA", airportIATA);
  if (supplierDriverGuid) params.append("supplierDriverGuid", supplierDriverGuid);

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  return getResponse(url);
}

export function saveBufferTime(request: SaveBufferTimeRequest): Promise<WebApiResponse<BufferTime>> {
  const url = `${EntitiesApiUrl}/api/buffer-time`;
  return post(url, request);
}

export function deleteBufferTime(id: string): Promise<WebApiResponse<any>> {
  const url = `${EntitiesApiUrl}/api/buffer-time/${id}`;
  return del(url);
}

export function enableBufferTime(id: string, websiteGuid: string): Promise<WebApiResponse<any>> {
  const url = `${EntitiesApiUrl}/api/buffer-time/${id}/enable?websiteGuid=${websiteGuid}`;
  return patch(url);
}

export function deletePickupInstructionsById(instructionId: string): Promise<WebApiResponse<any>> {
  const url = `${EntitiesApiUrl}/api/pickup-instructions/${instructionId}`;
  return del(url);
}

// Upcoming pickups for a specific airport (public)
export interface AirportUpcomingBooking {
  bookingGuid: string;
  externalRefNumber: string;
  pickupDateTimeLocal: string;
  pickupDateTimeUtc?: string;
  driverName?: string;
  partnerName?: string;
  regionName?: string;
  pax?: number;
  flightNumber?: string;
  isAirportPickup?: boolean;
  isAirportDropoff?: boolean;
  regionGuid?: string;
  pickupAirportIataCode?: string;
  dropoffAirportIataCode?: string;
  client?: {
    name?: string;
    phone?: string;
  };
  pickup?: {
    name?: string;
    address?: string;
  };
  dropoff?: {
    name?: string;
    address?: string;
  };
  distanceKm?: number;
  source?: string;
  notes?: string;
  ianaTimezoneId?: string;
  isCancelled?: boolean;
  bookingState?: 'NotSet' | 'Upcoming' | 'InProgress' | 'Finished' | 'Cancelled' | 'BidInProgress';
  flightTracking?: {
    liveArrivalTimeLocal?: string;
    scheduledArrivalTimeLocal?: string;
    status?: string;
    rawStatusCode?: string;
    lastRefreshStatus?: string;
    lastRefreshUtc?: string;
  };
  passengerInfo?: {
    firstName?: string;
    lastName?: string;
    salutation?: string;
    phone?: string;
  };
  vehicleInfo?: {
    name?: string;
    equivalent?: string;
    numberOfPassengers?: number;
    numberOfLuggages?: number;
    companyLogo?: string;
  };
  driverOverride?: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    plate?: string;
    carBrand?: string;
    carModel?: string;
    carColor?: string;
  };
  isExternalVehicle?: boolean;
  externalVehicleType?: string;
  externalVehicleCost?: number;
  externalVehicleCostCurrencyCode?: string;
  currency?: string;
}

export interface AirportUpcomingResponse {
  name?: string;
  logo?: string;
  currency?: string;
  contactEmail?: string;
  contactPhone?: string;
  contactWhatsapp?: string;
  airport?: {
    iata?: string;
    name?: string;
    city?: string;
  } | null;
  bookings: AirportUpcomingBooking[];
}

/**
 * Requests already in flight, keyed by what they are asking for. Two components mounting together ask
 * for the same thing (the navbar and the sidenav both load the dashboard configuration), and a page can
 * re-render while a slow request is still running. Sharing the pending promise collapses those into one
 * call instead of hitting the API twice and throwing one of the answers away.
 *
 * Only overlapping calls are shared: the entry is dropped as soon as the request settles, so a later
 * call still fetches fresh data.
 */
const inFlightRequests = new Map<string, Promise<any>>();

function dedupeInFlight<T>(key: string, request: () => Promise<T>): Promise<T> {
  const existing = inFlightRequests.get(key);
  if (existing) {
    return existing as Promise<T>;
  }

  const pending = (async () => {
    try {
      return await request();
    } finally {
      inFlightRequests.delete(key);
    }
  })();

  inFlightRequests.set(key, pending);
  return pending;
}

export async function getUpcomingBookingsForAirport(websiteGuid: string, airportCode?: string, regionGuid?: string, includeCancelled?: boolean, startDateUtc?: Date | null, endDateUtc?: Date | null): Promise<AirportUpcomingResponse> {
  let url = `${EntitiesApiUrl}/api/bookings/upcomingForAirport?websiteId=${websiteGuid}`;
  if (airportCode) {
    url += `&airportCode=${encodeURIComponent(airportCode)}`;
  }
  if (regionGuid) {
    url += `&regionGuid=${encodeURIComponent(regionGuid)}`;
  }
  if (includeCancelled) {
    url += `&includeCancelled=true`;
  }
  if (startDateUtc) {
    url += `&startDateUtc=${encodeURIComponent(startDateUtc.toISOString())}`;
  }
  if (endDateUtc) {
    url += `&endDateUtc=${encodeURIComponent(endDateUtc.toISOString())}`;
  }

  // Keyed on the URL before getResponse appends its cache-busting timestamp, so two concurrent
  // loads of the same view share one request.
  return dedupeInFlight(url, async () => {
    try {
      const result = await getResponse(url);
      // Endpoint returns ApiResponse.Ok(result) on server
      const payload = result?.result ?? result?.data ?? result;
      return payload;
    } catch (err) {
      console.error("getUpcomingBookingsForAirport error", err);
      return { bookings: [] } as AirportUpcomingResponse;
    }
  });
}

// (duplicate removed)

// export function getSuppliers(websiteGuid: string): Promise<SupplierInfo[]> {
//   //const requestOptions = getGetOptions();
//   const url = `${EntitiesApiUrl}/api/suppliers?websiteGuid=${websiteGuid}`;
//   return (
//     getResponse(url) //fetch(`${CbsApiUrl()}/api/Suppliers/getSuppliers?websiteGuid=${websiteGuid}`, requestOptions)
//       //.then(response => response.json())
//       .then((result) => {
//         //console.log(result);
//         if (result.success) {
//           return result.data as SupplierInfo[];
//         } else {
//           showError("An error occurred while loading suppliers. Please try again.");
//           return [] as SupplierInfo[];
//         }
//       })
//       .catch((error) => {
//         //console.log('error', error);
//         showError("An error occurred while loading suppliers. Please try again.");
//         return [] as SupplierInfo[];
//       })
//   );
// }

export function getWebsiteRegionsForSuppliers(websiteGuid: string): Promise<WebApiResponse<RegionInfo[]>> {
  const url = `${CbsApiUrl()}/api/Prices/getRegions/${websiteGuid}`;
  return getFromApi(url);
}

export function getWebsiteBookingDotComPricePlans(websiteGuid: string): Promise<WebApiResponse<BookingDotComPricePlan[]>> {
  const url = `${CbsOldApiUrl}/pricingApi/getPricePlans/${websiteGuid}/ba96cd7c-bdbb-4477-b6ca-25c863a768a3`;
  return getFromApi(url);
}

export function getFixedPricePlans(websiteGuid: string, priceplanguid: string): Promise<WebApiResponse<any>> {
  const url = `${CbsOldApiUrl}/pricingApi/getWebsiteFixedPrices/${websiteGuid}/${priceplanguid}`;
  return getFromApi(url);
}

export function updateSupplier(supplier: UpdateSupplierRequest) {
  //console.log("updateSupplier: ", supplier)
  const url = `${CbsApiUrl()}/api/Suppliers/updateSupplier`;
  return post(url, supplier);
}

export function updateSupplierServices(request: UpdateSupplierServicesRequest) {
  //console.log("updateSupplierServices: ", request)
  const url = `${CbsApiUrl()}/api/Suppliers/updateSupplierServices`;
  return post(url, request);
}

export function addSupplier(supplier: AddNewSupplierRequest) {
  //console.log("addNewSupplier: ", supplier)
  const url = `${CbsApiUrl()}/api/Suppliers/addNewSupplier`;
  return post(url, supplier);
}

export function inviteSupplier(supplierInfo: InviteSupplierRequest) {
  const url = `${CbsApiUrl()}/api/Suppliers/inviteSupplier`;
  console.log("supplier invite: ", supplierInfo);
  return post(url, supplierInfo);
}

export function getSupplierInvitation(invitationGuid: string) {
  const url = `${CbsApiUrl()}/api/Suppliers/getSupplierInvitation?id=${invitationGuid}`;
  //console.log("supplier invite: ", supplierInfo)
  return getResponse(url);
}

export function registerCompany(supplierInfo: InviteSupplierRequest) {
  const url = `${EntitiesApiUrl}/api/Account/register-website`;
  console.log("supplier invite: ", supplierInfo);
  return post(url, supplierInfo);
}
export function createNewWebsite(supplierInfo: InviteSupplierRequest) {
  const url = `${EntitiesApiUrl}/api/Websites`;
  return post(url, supplierInfo);
}

export function createClientOnSupplier(supplier: CreateClientRequest) {
  //console.log("addNewSupplier: ", supplier)
  const url = `${CbsApiUrl()}/api/Suppliers/createClientOnSupplier`;
  return post(url, supplier);
}

export function deleteSupplier(supplierId: string) {
  //console.log("deleteSupplier: ", supplier)
  const url = `${CbsApiUrl()}/api/Suppliers/deleteSupplier?supplierId=${supplierId}`;
  return post(url, null);
}

export function getSupplierPrice(bookingGuid: string, supplierGuid: string) {
  const url = `${CbsOldApiUrl}/priceCalculationApi/getSupplierPrices`;
  return post(url, { bookingGuid: bookingGuid, supplierGuid: supplierGuid });
}

export function getSupplierPriceV2(bookingGuid: string, supplierGuid: string): Promise<WebApiResponse<BookingPriceInfo>> {
  const url = `${ExternalApiUrl}/connect/getSupplierPrices`;
  return post(url, {
    bookingGuid: bookingGuid,
    supplierWebsiteGuid: supplierGuid,
  });
}

export function supplierAcceptBooking(bookingGuid: string) {
  const url = `${CbsApiUrl()}/api/bookings/AcceptBooking`;
  return post(url, { guid: bookingGuid });
}

export function supplierDeclineBooking(bookingGuid: string) {
  const url = `${CbsApiUrl()}/api/bookings/RejectBooking`;
  return post(url, { guid: bookingGuid });
}

export function removeSupplierFromBooking(request: { bookingGuid: string; supplierGuid?: string; supplierWebsiteId?: number }) {
  const url = `${CbsApiUrl()}/api/bookings/RemoveDriverOrSupplier`;
  return post(url, request);
}
//#endregion Suppliers API

//#region Create / Update / View Booking
export function autoComplete(searchInput, lat, lng) {
  const url = `${EntitiesApiUrl}/api/Place/autocomplete?text=${searchInput}&lat=${lat ?? ""}&lng=${lng ?? ""}`;
  return getResponse(url);
}

export function getIanaTimezoneByLatLng(lat: number, lng: number): Promise<ApiResponse<string>> {
  const url = `${EntitiesApiUrl}/api/Place/getIanaTimezoneByLatLng?lat=${lat}&lng=${lng}`;
  return getResponse(url);
}

export function getBookingPrice(request) {
  const url = `${ExternalApiUrl}/connect/getBookingPrice`;
  return post(url, request);
}

export function getNewBookingDetails_Old() {
  const url = `${CbsOldApiUrl}/BookingApi/GetBooking/website`;
  return getResponse(url);
}
export function getNewBookingDetails_New() {
  const url = `${CbsApiUrl()}/BookingApi/GetBooking/website`;
  return getResponse(url);
}

export function getNewBookingDetails(guid: string) {
  //http://localhost:51438/BookingApi/GetBooking/2f035a39-6910-489a-a68f-bbd71cef4bcd
  const url = `${CbsApiUrl()}/BookingApi/GetBooking/${guid}`;
  return getResponse(url);
}

export function saveBooking_Old(bookingCreatePayload: any) {
  //console.log("SaveBooking payload: ", bookingCreatePayload);
  const url = `${CbsOldApiUrl}/BookingApi/SaveBooking`;
  return post(url, bookingCreatePayload);
}
export function saveBooking_New(bookingCreatePayload: any) {
  //console.log("SaveBooking payload: ", bookingCreatePayload);
  const url = `${CbsApiUrl()}/api/AdminBookings/bookings`;
  return post(url, bookingCreatePayload);
}
export function importBookings(bookingCreatePayload: any) {
  //console.log("SaveBooking payload: ", bookingCreatePayload);
  const url = `${CbsApiUrl()}/api/AdminBookings/import`;
  return post(url, bookingCreatePayload);
}
export function saveClientBooking_Old(bookingCreatePayload: any) {
  //console.log("SaveClientBooking payload: ", bookingCreatePayload);
  const url = `${CbsOldApiUrl}/BookingApi/SaveClientBooking`;
  return post(url, bookingCreatePayload);
}

export function saveClientBooking(saveBookingPayload: any) {
  const url = `${CbsApiUrl()}/api/clientBookings/SaveBooking`;
  return post(url, saveBookingPayload);
}

export function updateBookingDriverStatus(model: SetBookingDriverStatusPayload) {
  const url = `${CbsOldApiUrl}/BookingApi/SetBookingDriverStatuses`;
  return post(url, model);
}

export function getBookingPenalty(bookingGuid: string) {
  const url = `${CbsOldApiUrl}/BookingApi/getBookingPenalty`;
  return post(url, { bookingGuid: bookingGuid });
}

export function setBookingPenalty(bookingGuid: string, amount: number, description: string) {
  const url = `${CbsOldApiUrl}/BookingApi/SetBookingPenalty`;
  return post(url, {
    bookingGuid: bookingGuid,
    amount: amount,
    reason: description,
  });
}

export function getBookingCancellationInfo(guid: string) {
  const url = `${CbsApiUrl()}/ABookingApi/GetBookingCancellationInfo/${guid}`;
  return getResponse(url);
}

export function cancelBooking(bookingCancellationPayload: BookingCancellationRequest) {
  console.log("cancelBooking", bookingCancellationPayload);
  const url = `${CbsApiUrl()}/ABookingApi/CancelBooking`;
  return post(url, bookingCancellationPayload);
}

export function getBookingChangesLog(bookingGuid: string) {
  const url = `${CbsApiUrl()}/api/bookings/getBookingChangesLogInfo/${bookingGuid}`;
  return getResponse(url);
}

export function getBookingChangesLog_Old(bookingGuid: string) {
  const url = `${CbsOldApiUrl}/bookingApi/getBookingChangesLogInfo/${bookingGuid}`;
  return getResponse(url);
}

export function getBookingDriverEvents(bookingGuid: string) {
  const url = `${CbsApiUrl()}/api/bookings/${bookingGuid}/driver-events`;
  return getResponse(url);
}

export function getBookingsCacheInfo() {
  const url = `${CbsApiUrl()}/api/bookings/getBookingsCacheInfo`;
  return getResponse(url, { timeoutInSeconds: 300 });
}

export function getBookingVolumeCacheInfo() {
  const url = `${CbsApiUrl()}/api/bookings/getBookingVolumeCacheInfo`;
  return getResponse(url);
}

export function reloadBookingsInCache(numberOfDays) {
  const url = `${CbsApiUrl()}/api/bookings/refreshBookingsCacheForNextDays/${numberOfDays}`;
  return getResponse(url);
}

export function resetBookingsCache() {
  const url = `${CbsApiUrl()}/api/bookings/resetBookingsCache`;
  return getResponse(url);
}
export function getBookingCountSqlAndMongo(start, end, byCreatedAt) {
  const url = `${CbsApiUrl()}/api/bookings/different-quantities?start=${start}&end=${end}&byCreatedAt=${byCreatedAt}`;
  return getResponse(url);
}
export function reindexBooking(start, end, byCreatedAt, isForce) {
  const url = `${CbsApiUrl()}/api/bookings/ReindexBookings?start=${start}&end=${end}&byCreatedAt=${byCreatedAt}&forceReindex=${isForce}`;
  return getResponse(url);
}
export function startReindexBooking(start, end, byCreatedAt, isForce, totalItems) {
  const url = `${CbsApiUrl()}/api/bookings/ReindexBookings/start`;
  return post(url, { start, end, byCreatedAt, forceReindex: isForce, totalItems });
}
export function getReindexStatus(jobId) {
  const url = `${CbsApiUrl()}/api/bookings/ReindexBookings/status/${jobId}`;
  return getResponse(url);
}
export function stopReindexBooking(jobId) {
  const url = `${CbsApiUrl()}/api/bookings/ReindexBookings/stop/${jobId}`;
  return post(url, {});
}
//#endregion

/* Live Map API */

export function getActiveDrivers(payload: { websiteGuid: string; timeoutInSeconds: number }) {
  const url = `${CbsApiUrl(false)}/drivers/getActiveDrivers`;
  return post(url, payload);
}

/** Driver bot records (live and dry run) for the live map. docs/cbs-admin/features/driver-bot/driver_bot.md §13a */
export function getActiveBots(payload: { websiteGuid: string }) {
  const url = `${CbsApiUrl(false)}/drivers/getActiveBots`;
  return post(url, payload);
}

/** Stored route of one bot ride (approach + ride) for "Show route" on the live map. No Google call. */
export function getBotRoute(bookingGuid: string) {
  const url = `${CbsApiUrl(false)}/drivers/getBotRoute?bookingGuid=${encodeURIComponent(bookingGuid)}`;
  return post(url, {});
}

export function stopDriverBot(bookingGuid: string) {
  const url = `${CbsApiUrl(false)}/drivers/stopBot?bookingGuid=${encodeURIComponent(bookingGuid)}`;
  return post(url, {});
}

/* END: Live Map API */
/* Dispatch API */

export function getDriverLocation(hash: string) {
  const url = `${CbsApiUrl()}/drivers/getDriverLocation/${hash}`;
  return getResponse(url);
}

export function getBookingDetails(guid: string) {
  const url = `${CbsApiUrl()}/api/bookings/GetBooking/${guid}`;
  //`${CbsOldApiUrl}/BookingApi/GetBookingDetailsForAdmin/${guid}`;
  return getResponse(url);
}

/**
 * Batch form of getBookingDetails. The dispatch board is notified about every booking that changes, and
 * a Booking.com import changes thousands - asking one at a time cost a request and a single-row SQL read
 * each. The server caps a batch, so callers chunk.
 */
export function getBookingsByGuids(guids: string[]) {
  const url = `${CbsApiUrl()}/api/bookings/GetBookings`;
  return postToApi(url, guids);
}

/**
 * Dashboard configuration response interface
 */
export interface DashboardConfiguration {
  enableMarketplace: boolean;
  marketplaceEnabledRegions: string[];
  enableChat: boolean;
  /** Master flag for phone support (IVR, operators, outbound VoIP). Gates the website Support tab. */
  enablePhoneSupport: boolean;
  enableDispatchPriceBreakdown: boolean;
  /** Whether driver/supplier scoring is enabled for this website (gates the Rankings link, etc.). */
  enableDriverSupplierScoring: boolean;
  enableQuotations: boolean;
  /** Region restrictions for the current user. Empty = full access. */
  userRegionConfigs: AllowedRegionConfig[];
  /** Per-website customer booking-page URL template (WebsiteAdvanced.ClientBookingUrl), e.g.
   *  "https://app.example.com/#/cb/{guid}". Single source of truth for client booking links. */
  clientBookingUrl: string;
  /** When true, OTAs receive this website's support number instead of the driver's, so the per-booking
   *  driver Phone field is disabled in the dispatch UI (the server overwrites it regardless). */
  shareSupportPhoneWithOta: boolean;
  /** False when the request failed and every flag above is a fallback default rather than the website's
   *  real configuration. Callers that only *hide* things can ignore this; callers that tell the user
   *  WHY something is hidden must not report an all-false fallback as "the feature is switched off". */
  loaded: boolean;
}

// Cached per-website client booking-page URL template (from GetDashboardConfiguration). Read by
// buildClientBookingLink so client links can be built anywhere without hardcoding a reservation URL.
let cachedClientBookingUrlTemplate = "";

/**
 * Build the client-facing booking link for a booking guid from the configured ClientBookingUrl template
 * — the single source of truth (WebsiteAdvanced.ClientBookingUrl, surfaced via GetDashboardConfiguration).
 * Brace-tolerant on the {guid} placeholder (also handles a doubled {{guid}}). Falls back to the legacy
 * reservation URL when no template is configured/loaded (e.g. a route that never loaded the dashboard config).
 */
export function buildClientBookingLink(bookingGuid: string): string {
  const template = (cachedClientBookingUrlTemplate || "").trim();
  if (template) {
    const placeholder = /\{+\s*guid\s*\}+/i;
    return placeholder.test(template)
      ? template.replace(placeholder, bookingGuid)
      : `${template.replace(/\/+$/, "")}/${bookingGuid}`;
  }
  return `${window.location.origin}#/reservation/${bookingGuid}`;
}

/**
 * Get dashboard configuration including marketplace-enabled regions.
 * Called by Dashboard on mount to determine which bookings can be sent to marketplace.
 * Returns empty configuration on error (safe fallback - no marketplace controls shown).
 */
export function getDashboardConfiguration(websiteGuid: string): Promise<DashboardConfiguration> {
  return dedupeInFlight(`dashboardConfiguration:${websiteGuid}`, () => fetchDashboardConfiguration(websiteGuid));
}

async function fetchDashboardConfiguration(websiteGuid: string): Promise<DashboardConfiguration> {
  try {
    const url = `${CbsApiUrl()}/api/bookings/GetDashboardConfiguration`;
    const response = await post(url, { websiteGuid });
    if (response?.success && response?.data) {
      cachedClientBookingUrlTemplate = response.data.clientBookingUrl ?? "";
      return {
        enableMarketplace: response.data.enableMarketplace ?? false,
        marketplaceEnabledRegions: response.data.marketplaceEnabledRegions || [],
        enableChat: response.data.enableChat ?? false,
        enablePhoneSupport: response.data.enablePhoneSupport ?? false,
        enableDispatchPriceBreakdown: response.data.enableDispatchPriceBreakdown ?? false,
        enableDriverSupplierScoring: response.data.enableDriverSupplierScoring ?? false,
        enableQuotations: response.data.enableQuotations ?? false,
        userRegionConfigs: response.data.userRegionConfigs || [],
        clientBookingUrl: cachedClientBookingUrlTemplate,
        shareSupportPhoneWithOta: response.data.shareSupportPhoneWithOta ?? false,
        loaded: true
      };
    }
    console.error("Failed to get dashboard configuration:", response?.errorMessages);
    return { enableMarketplace: false, marketplaceEnabledRegions: [], enableChat: false, enablePhoneSupport: false, enableDispatchPriceBreakdown: false, enableDriverSupplierScoring: false, enableQuotations: false, userRegionConfigs: [], clientBookingUrl: "", shareSupportPhoneWithOta: false, loaded: false };
  } catch (error) {
    console.error("Error fetching dashboard configuration:", error);
    return { enableMarketplace: false, marketplaceEnabledRegions: [], enableChat: false, enablePhoneSupport: false, enableDispatchPriceBreakdown: false, enableDriverSupplierScoring: false, enableQuotations: false, userRegionConfigs: [], clientBookingUrl: "", shareSupportPhoneWithOta: false, loaded: false };
  }
}

export function getDrivers(websiteGuid: string) {
  const url = `${CbsApiUrl()}/api/drivers?websiteGuid=${websiteGuid}`;
  return getResponse(url);
}

export function getDriverInfo(driverGuid: string) {
  const url = `${CbsApiUrl()}/api/drivers/${driverGuid}`;
  return getResponse(url);
}
export function createDriver(request: SaveDriverRequest) {
  const url = `${CbsApiUrl()}/api/drivers`;
  return post(url, request);
}

export function saveDriver(id: string, request: SaveDriverRequest) {
  const url = `${CbsApiUrl()}/api/drivers/${id}`;
  return put(url, request);
}

export interface UpdateDriverStatusRequest {
  websiteGuid: string;
  isDisabled: boolean;
  isBlocked: boolean;
}

export function updateDriverStatus(id: string, request: UpdateDriverStatusRequest) {
  const url = `${CbsApiUrl()}/api/drivers/${id}/status`;
  return put(url, request);
}

export function deleteDriver(id: string) {
  const url = `${CbsApiUrl()}/api/drivers/${id}`;
  return del(url);
}

export interface DriverBasicInfo {
  id: string;
  fullName: string;
}

export function getDriversByRegion(regionId: string, websiteId: string): Promise<ApiResponse<DriverBasicInfo[]>> {
  const url = `${CbsApiUrl()}/api/drivers/region/${regionId}?websiteId=${websiteId}`;
  return getResponse(url);
}

export interface AssignDriverByGreeterRequest {
  bookingGuid: string;
  websiteGuid: string;
  driverGuid: string;
  reason?: string;
}

export interface AssignDriverByGreeterResponse {
  bookingGuid: string;
  driverGuid: string;
  driverName: string;
  message: string;
}

export function assignDriverByGreeter(request: AssignDriverByGreeterRequest): Promise<ApiResponse<AssignDriverByGreeterResponse>> {
  const url = `${CbsApiUrl()}/api/bookings/AssignDriverForGreeter`;
  return post(url, request);
}

export interface UpdateDriverOverrideRequest {
  firstName?: string;
  lastName?: string;
  phone?: string;
  plate?: string;
  carBrand?: string;
  carModel?: string;
  carColor?: string;
}

export function updateDriverOverride(bookingGuid: string, data: UpdateDriverOverrideRequest): Promise<ApiResponse<any>> {
  const url = `${CbsApiUrl()}/api/bookings/${bookingGuid}/driver-override`;
  return put(url, data);
}

export interface MarkExternalVehicleRequest {
  vehicleType?: string;
  cost?: number;
  costCurrencyCode?: string;
  driverFirstName?: string;
  driverLastName?: string;
  driverPhone?: string;
  driverPlate?: string;
}

export function markExternalVehicle(bookingGuid: string, data: MarkExternalVehicleRequest): Promise<ApiResponse<any>> {
  const url = `${CbsApiUrl()}/api/bookings/${bookingGuid}/mark-external-vehicle`;
  return post(url, data);
}

export function clearExternalVehicle(bookingGuid: string): Promise<ApiResponse<any>> {
  const url = `${CbsApiUrl()}/api/bookings/${bookingGuid}/mark-external-vehicle`;
  return del(url);
}

export function markZeroDownCase(bookingGuid: string): Promise<ApiResponse<any>> {
  const url = `${CbsApiUrl()}/api/bookings/${bookingGuid}/zero-down?isZeroDown=true`;
  return patch(url);
}

export function clearZeroDownCase(bookingGuid: string): Promise<ApiResponse<any>> {
  const url = `${CbsApiUrl()}/api/bookings/${bookingGuid}/zero-down?isZeroDown=false`;
  return patch(url);
}

export function restoreBooking(bookingGuid: string): Promise<ApiResponse<any>> {
  const url = `${CbsApiUrl()}/api/bookings/${bookingGuid}/restore`;
  return post(url);
}

export interface BookingDriverDetails {
  firstName?: string;
  lastName?: string;
  phone?: string;
}

export function getBookingDriverDetails(bookingGuid: string): Promise<ApiResponse<BookingDriverDetails>> {
  const url = `${CbsApiUrl()}/api/bookings/${bookingGuid}/driver-details`;
  return getResponse(url);
}

export function createVehicle(id: string, request: SaveVehicleRequest) {
  const url = `${CbsApiUrl()}/api/drivers/${id}/cars`;
  return post(url, request);
}

export function getVehicleInfo(driverId: string, vehicleId: string) {
  const url = `${CbsApiUrl()}/api/drivers/${driverId}/cars/${vehicleId}`;
  return getResponse(url);
}

export function saveVehicle(driverId: string, vehicleId: string, request: SaveVehicleRequest) {
  const url = `${CbsApiUrl()}/api/drivers/${driverId}/cars/${vehicleId}`;
  return put(url, request);
}

export function deleteVehicle(driverId: string, vehicleId: string) {
  const url = `${CbsApiUrl()}/api/drivers/${driverId}/cars/${vehicleId}`;
  return del(url);
}

export function getMeetAndGreet(bookingGuid: string) {
  const url = `${CbsOldApiUrl}/bookings/ws/${bookingGuid}`;
  return url;
}

export interface SaveDriverRequest {
  websiteGuid: string;
  firstName: string;
  lastName: string;
  phone: string;
  gender: number;
  paymentMethod: string;
  paymentAccount: string;
  paymentPeriod: string;
  nationality: string;
  ianaTimezone: string;
  additionalInternalInfo: string;
  additionalClientInfo: string;
  photo: Photo;
}

interface Photo {
  id: string;
  type: string;
  publicUrl: string;
}

export interface Booking {
  guid: string;
  customerId: string;
  websiteId: number;
  websiteGuid: string;
  createdAtUtc?: string;
  pickupDateTimeMoment: string;
  gmtPickupDateTimeMoment: string;
  localPickupDateTime: string;
  gmtPickupDateTime: string;
  from: LocationInfo;
  to: LocationInfo;
  metricUnit: number;
  distanceInMiles: number;
  distanceInKilometers: number;
  durationInMinutes: number;
  customerName: string;
  accountName: string;
  companyName?: string;
  isSupplierBooking: boolean;
  supplierStatus: number;
  isProviderBooking: boolean;
  driverUrl: string;
  clientUrl: string;
  driver: DriverInfo;
  adminStatus: number;
  bookingType: number;
  isCancelled: boolean;
  externalRefNumber: string;
  showProfitIgnoringTax: boolean;
  driverAcceptanceStatus: number;
  driverOnRouteStatus: number;
  driverResponses: number;
  isBidInProgress: boolean;
  source: string;
  carClassName: string;
  maxPassengers?: number;
  maxLuggage?: number;
  isInProgress: boolean;
  isFinished: boolean;
  status: string;
  statusClass: string;
  timeLeft: number;
  bookingPrice: number;
  paymentStatusEnum: number;
  paymentStatusString: string;
  totalPrice: number;
  totalIncome: number;
  totalTaxes: number;
  profit: number;
  canAssignDriver: boolean;
  canAcceptReject: boolean;
  canRestore: boolean;
  currency: string;
  paymentMethod: string;
  region: string;
  regionGuid?: string;
  priceDescription?: string;
  driverCurrency: string;
  hasVat: boolean;
  numberOfExtras: number;
  passengerDetails: PassengerDetails;
  customerNotes?: string;
  internalNotes?: string;
  driverNotes?: string;
  isSupportOperator: boolean;
  isAdmin: boolean;
  isDispatcher: boolean;
  isDataEntry: boolean;
  isAutoAssigned?: boolean;
}

interface AirportInfo {
  iata: string;
  name: string;
  waitingTimeInMinutes?: number;
}

interface LocationInfo {
  isAirport: boolean;
  airportInfo?: AirportInfo;
  address: string;
  addressName: string;
  latLng: LatLng;
}

interface PassengerDetails {
  salutation: string;
  firstName: string;
  phone: string;
  adultsNumber: number;
  adults: number;
  hasMeetAndGreet: boolean;
  flightNumber?: string;
}

export function getNextBookings(
  websiteGuid: string,
  numberOfDays: string | null,
  startDate: Moment,
  endDate: Moment,
  statuses: string[],
  searchText: string
): Promise<Booking[]> {
  const url = `${CbsApiUrl()}/api/bookings/GetNextBookingsForAdmin`;
  const start = startDate ? encodeURI(startDate.toISOString()) : null;
  const end = endDate ? encodeURI(endDate.toISOString()) : null;
  const timezone = start && end ? "local" : null;
  if (start && end) {
    numberOfDays = null;
  }

  const payload = {
    numberOfDays: numberOfDays,
    startDate: start,
    endDate: end,
    timezone: timezone,
    websitesGuids: [websiteGuid],
    status: "all",
    statuses: statuses,
    searchText: searchText,
  };

  return post(url, payload).then((data) => {
    if (data) {
      return data.data;
    }
  });
}
export function getNextBookingsInMongo(
  websiteGuid: string,
  numberOfDays: string | null,
  startDate: Moment,
  endDate: Moment,
  statuses: string[],
  searchText: string
): Promise<Booking[]> {
  const url = `${EntitiesApiUrl}/api/bookings/GetNextBookingsForAdmin`;
  const start = startDate ? encodeURI(startDate.toISOString()) : null;
  const end = endDate ? encodeURI(endDate.toISOString()) : null;
  const timezone = start && end ? "local" : null;
  if (start && end) {
    numberOfDays = null;
  }

  const payload = {
    numberOfDays: numberOfDays,
    startDate: start,
    endDate: end,
    timezone: timezone,
    websitesGuids: [websiteGuid],
    status: "all",
    statuses: statuses,
    searchText: searchText,
  };

  return post(url, payload).then((data) => {
    if (data) {
      return data.data;
    }
  });
}
export function getNextBookingsBetweenDates(websiteGuid: string, start: string, end: string) {
  //console.log("getNextBookingsBetweenDates");
  //const url = `${CbsOldApiUrl}/BookingApi/GetNextBookingsForAdmin`;
  const url = `${CbsApiUrl()}/api/bookings/bookingApi/GetNextBookingsForAdmin`;
  const payload = {
    startDate: start,
    endDate: end,
    websitesGuids: [websiteGuid],
    status: "all",
  };

  return post(url, payload).then((data) => {
    if (data) {
      //console.log("getNextBookings result", data);
      //return data.items;
      return data.result;
    }
  });
}

export function getClientBookings(websiteGuid: string, start: string, end: string) {
  //console.log(`getClientBookings ${websiteGuid} ${start} ${end}`);
  const url = `${CbsApiUrl()}/api/clientBookings/GetBookings`;
  const payload = {
    startDate: start,
    endDate: end,
    websitesGuids: [websiteGuid],
    numberOfDays: 0,
    searchText: null,
    pageIndex: 0,
    pageSize: 0,
  };

  return post(url, payload);
}

export interface DriverAcceptBookingRequest {
  bookingGuid: string;
  driverGuid: string;
  price?: number;
  location?: LatLng;
  driverCarId?: string;
}

export function acceptDriverBooking(payload: DriverAcceptBookingRequest): Promise<SimpleApiResponse> {
  const url = `${CbsApiUrl()}/api/driverBookings/AcceptDriverBooking`;
  return post(url, payload);
}

export interface DriverRejectBookingRequest {
  driverGuid: string;
  bookingGuid: string;
  reason: string;
  location?: LatLng;
  driverAddress?: string;
}

export function rejectDriverBooking(payload: DriverRejectBookingRequest): Promise<SimpleApiResponse> {
  const url = `${CbsApiUrl()}/api/driverBookings/RejectDriverBooking`;
  return post(url, payload);
}

export function updateDriverRouteStatus(bookingId: number, websiteGuid: string, onRouteStatus: number) {
  //console.log(`updateDriverRouteStatus ${bookingId} ${websiteGuid} ${onRouteStatus}`);
  const url = `${CbsApiUrl()}/api/driverBookings/UpdateDriverBookingStatus`;
  const payload = {
    bookingId: bookingId,
    websitesGuids: [websiteGuid],
    onRouteStatus: onRouteStatus,
  };

  return post(url, payload);
}

export function getClientBookingCancellationInfo(bookingGuid: string): Promise<ClientBookingCancellationRequest> {
  const url = `${CbsApiUrl()}/api/clientBookings/GetBookingCancellationInfo?bookingGuid=${bookingGuid}`;
  return get(url);
}

type ClientBookingCancellationRequest = {
  guid: string;
  cancelationCost: number;
  currency: string;
  hash: string;
  reason: string;
  reasonDescription: string;
};

export function cancelClientBooking(bookingCancellationPayload: ClientBookingCancellationRequest) {
  //console.log("cancelClientBooking", bookingCancellationPayload);
  const url = `${CbsApiUrl()}/api/clientBookings/CancelBooking`;
  return post(url, bookingCancellationPayload);
}

export function getBooking(guid: string) {
  const url = `${CbsApiUrl()}/api/bookings/GetBooking/${guid}`;
  return get(url);
}

interface DriversToAssignResponse {
  drivers: Driver[];
  bookingGuid: string;
  driverGuid: string;
  serviceGuid: string;
  sendConfirmationEmailToDriver: boolean;
  sendConfirmationSmsToDriver: boolean;
  sendConfirmationEmailToClient: boolean;
  enableDispatchDriverAvailability?: boolean;
  dispatchDriverAvailabilityBufferMinutes?: number;
}

interface Driver {
  id: string;
  guid: string;
  fullName: string;
  currency: string;
  description?: string;

  isHired: boolean;
  isSupplier: boolean;
  isCoveringRegion?: boolean;
  email: string;
  phone: string;
  carClass: string;
  regions: PartnerRegion[];
}

interface PartnerRegion {
  id: string;
  name: string;
}

export interface AssignmentSuggestionCandidateRequest {
  partnerGuid: string;
  isSupplier: boolean;
  currency?: string | null;
}

export interface AssignmentPriceSuggestionDto {
  suggestedAmount?: number | null;
  suggestedCurrency?: string | null;
  suggestedAmountInBookingCurrency?: number | null;
  bookingCurrency?: string | null;
  strategy?: string | number | null;
  confidenceScore?: number;
  confidenceBand?: string | number | null;
  sampleSize?: number;
  lastAssignedAmount?: number | null;
  lastAssignedCurrency?: string | null;
  lastAssignedAtUtc?: string | null;
  averageAmount?: number | null;
  medianAmount?: number | null;
  averageMarginAmount?: number | null;
  averageMarginPercentage?: number | null;
  typicalLowAmount?: number | null;
  typicalHighAmount?: number | null;
  consistencyBand?: string | number | null;
  isFallback?: boolean;
  shouldAutoFill?: boolean;
  reason?: string | null;
  recentAssignments?: Array<{
    bookingGuid: string;
    amount?: number | null;
    currency?: string | null;
    amountInBookingCurrency?: number | null;
    assignedAtUtc?: string | null;
  }>;
}

export interface AssignmentProfitWarningDto {
  level?: string | number | null;
  expectedMarginAmount?: number | null;
  expectedMarginPercentage?: number | null;
  currency?: string | null;
  message?: string | null;
}

export interface PartnerAssignmentSuggestionResultDto {
  partnerGuid: string;
  suggestion?: AssignmentPriceSuggestionDto | null;
  profitWarning?: AssignmentProfitWarningDto | null;
}

export interface GetAssignmentPriceSuggestionsResponse {
  bookingGuid: string;
  enableDispatchPriceSuggestions: boolean;
  enableDispatchProfitWarnings: boolean;
  dispatchPriceSuggestionMode: string | number;
  items: PartnerAssignmentSuggestionResultDto[];
}

export interface DriverAvailabilityConflictDetailDto {
  bookingGuid: string;
  customerId?: string | null;
  pickupUtc: string;
  durationInMinutes?: number;
  direction?: string | null;
  locationRole?: string | null;
  locationAddress?: string | null;
}

export interface DriverAvailabilityForDispatchItemDto {
  driverGuid: string;
  isAvailable: boolean;
  conflictCount: number;
  nearestConflictPickupUtc?: string | null;
  nearestConflictDirection?: string | null;
  nearestConflictCustomerId?: string | null;
  nearestConflictBookingGuid?: string | null;
  nearestConflictLocationRole?: string | null;
  nearestConflictLocationAddress?: string | null;
  details?: DriverAvailabilityConflictDetailDto[];
}

export interface GetDriverAvailabilityForDispatchResponse {
  bookingGuid: string;
  items: DriverAvailabilityForDispatchItemDto[];
}

export interface UpdateBookingPaymentRequest {
  bookingGuid: string;
  newPayment: number;
}

export function getDriversToAssign(bookingGuid: string): Promise<ApiResponse<DriversToAssignResponse>> {
  const url = `${CbsApiUrl()}/api/bookings/GetDriversToAssign`;
  const payload = { bookingGuid: bookingGuid };
  return post(url, payload);
}

export function getAssignmentPriceSuggestions(
  bookingGuid: string,
  candidates: AssignmentSuggestionCandidateRequest[]
): Promise<ApiResponse<GetAssignmentPriceSuggestionsResponse>> {
  const url = `${CbsApiUrl()}/api/bookings/GetAssignmentPriceSuggestions`;
  return post(url, { bookingGuid, candidates });
}

export function getDriverAvailabilityForDispatch(
  bookingGuid: string,
  driverGuids: string[]
): Promise<ApiResponse<GetDriverAvailabilityForDispatchResponse>> {
  const url = `${CbsApiUrl()}/api/bookings/GetDriverAvailabilityForDispatch`;
  return post(url, { bookingGuid, driverGuids });
}

export function removeDriverFromBooking(request: { bookingGuid: string; driverGuid: string; supplierGuid: string }) {
  const url = `${CbsApiUrl()}/api/bookings/RemoveDriverOrSupplier`;
  return post(url, request);
}

export function assignDriver(assignDriverRequest: AssignDriverRequest) {
  const url = `${CbsApiUrl()}/api/bookings/AssignDriver`;
  return post(url, assignDriverRequest);
}

export function updateBookingPayment(updateBookingPaymentRequest: UpdateBookingPaymentRequest) {
  const url = `${CbsApiUrl()}/api/bookings/UpdateBookingPayment`;
  return patch(url, updateBookingPaymentRequest);
}

export function assignOneTimeDriver(assignDriverRequest: AssignDriverRequest) {
  const url = `${CbsApiUrl()}/api/bookings/AssignOneTimeDriver`;
  return post(url, assignDriverRequest);
}

export function sendSms(sendSmsModel: SendSmsRequest) {
  //console.log("sending sms...", sendSmsModel);
  const url = `${CbsOldApiUrl}/SmsApi/sendSmsMessage`;
  return post(url, sendSmsModel);
}

/* END: Dispatch API */

// #region Website API
export function getGeneralWebsite(guid: string) {
  const url = `${EntitiesApiUrl}/api/Websites/${guid}/general`;
  return get(url);
}
export function updateGeneralWebsite(guid: string, data: object) {
  const url = `${EntitiesApiUrl}/api/Websites/${guid}/general`;
  return put(url, data);
}
export const getWebsiteNotification = async (guid) => {
  const url = `${EntitiesApiUrl}/api/Websites/${guid}/notification`;
  return get(url);
};

export const updateWebsiteNotification = async (guid, data) => {
  const url = `${EntitiesApiUrl}/api/Websites/${guid}/notification`;
  return put(url, data);
};
export const getWebsiteSmtpSms = async (websiteId) => {
  const url = `${EntitiesApiUrl}/api/Websites/${websiteId}/smtpsms`;
  return get(url);
};

export const updateWebsiteSmtpSms = async (websiteId, data) => {
  const url = `${EntitiesApiUrl}/api/websites/${websiteId}/smtpsms`;
  return put(url, data);
};

export const testSmtpConnection = async (data) => {
  const url = `${EntitiesApiUrl}/api/websites/smtpsms/test-smtp`;
  return post(url, data);
};

// Validates the SMS provider credentials and returns the account's owned numbers.
// Resolves to the OperationResult body ({ success, result: [{number,label}], successMessages });
// throws on failure (error.response.data.errorMessages holds the provider message).
export const testSmsConfiguration = async (websiteId, data) => {
  const url = `${EntitiesApiUrl}/api/websites/${websiteId}/smtpsms/test-sms`;
  return post(url, data);
};
export const getWebsiteBranding = async (websiteId) => {
  const url = `${EntitiesApiUrl}/api/websites/${websiteId}/branding`;
  return get(url);
};

export const updateWebsiteBranding = async (websiteId, data) => {
  const url = `${EntitiesApiUrl}/api/websites/${websiteId}/branding`;
  return put(url, data);
};
export const getWebsiteLoyalty = async (websiteId) => {
  const url = `${EntitiesApiUrl}/api/Websites/${websiteId}/loyalty`;
  return get(url);
};

export const updateWebsiteLoyalty = async (websiteId, data) => {
  const url = `${EntitiesApiUrl}/api/Websites/${websiteId}/loyalty`;
  return put(url, data);
};
export const getWebsitePaymentAndBilling = async (websiteId) => {
  const url = `${EntitiesApiUrl}/api/Websites/${websiteId}/payment-and-billing`;
  return get(url);
};

export const updateWebsitePaymentAndBilling = async (websiteId, data) => {
  const url = `${EntitiesApiUrl}/api/Websites/${websiteId}/payment-and-billing`;
  return put(url, data);
};
export const getBookingDotComConfig = async (websiteId: string): Promise<BookingDotComConfig | null> => {
  const url = `${EntitiesApiUrl}/api/Websites/${websiteId}/bookingdotcom-configuration`;
  return get(url);
};

export const getBookingDotComBasicConfig = async (websiteId: string): Promise<BookingDotComBasicConfig | null> => {
  const url = `${EntitiesApiUrl}/api/Websites/${websiteId}/bookingdotcom-basic-configuration`;
  return get(url);
};

export const updateBookingDotComConfig = async (websiteId: string, data: Partial<BookingDotComConfig>) => {
  const url = `${EntitiesApiUrl}/api/Websites/${websiteId}/bookingdotcom-configuration`;
  return put(url, data);
};

export const registerBookingDotComWebhook = async (websiteId: string) => {
  const url = `${ExternalApiUrl}/BookingDotCom/webhook/register?websiteId=${websiteId}`;
  return post(url, {});
};

export const deregisterBookingDotComWebhook = async (websiteId: string) => {
  const url = `${ExternalApiUrl}/BookingDotCom/webhook/register?websiteId=${websiteId}`;
  return del(url);
};
export const getWebsiteTermsAndConditions = async (websiteId) => {
  const url = `${EntitiesApiUrl}/api/Websites/${websiteId}/terms-and-conditions`;
  return get(url);
};

export const updateWebsiteTermsAndConditions = async (websiteId, data) => {
  const url = `${EntitiesApiUrl}/api/Websites/${websiteId}/terms-and-conditions`;
  return put(url, data);
};

export const getWebsiteAdvanced = async (websiteId) => {
  const url = `${EntitiesApiUrl}/api/Websites/${websiteId}/advanced`;
  return get(url);
};

export const updateWebsiteAdvanced = async (websiteId, data) => {
  const url = `${EntitiesApiUrl}/api/Websites/${websiteId}/advanced`;
  return put(url, data);
};

export const getWebsiteDriverIdentity = async (websiteId) => {
  const url = `${EntitiesApiUrl}/api/Websites/${websiteId}/driver-identity`;
  return get(url);
};

export const updateWebsiteDriverIdentity = async (websiteId, data) => {
  const url = `${EntitiesApiUrl}/api/Websites/${websiteId}/driver-identity`;
  return put(url, data);
};

// Driver Bot per-website config (docs/cbs-admin/features/driver-bot/driver_bot.md §11.1). Super-admin only.
export const getWebsiteDriverBot = async (websiteId) => {
  const url = `${EntitiesApiUrl}/api/Websites/${websiteId}/driver-bot`;
  return get(url);
};

export const updateWebsiteDriverBot = async (websiteId, data) => {
  const url = `${EntitiesApiUrl}/api/Websites/${websiteId}/driver-bot`;
  return put(url, data);
};

export const getWebsiteSupport = async (websiteId) => {
  const url = `${EntitiesApiUrl}/api/Websites/${websiteId}/support`;
  return get(url);
};

/** Paginated call log (inbound + outbound) for a website. */
export const getSupportCalls = async (websiteId, params?: { page?: number; pageSize?: number; direction?: string; search?: string }) => {
  const url = `${EntitiesApiUrl}/api/Websites/${websiteId}/support/calls`;
  return get(url, params);
};

/** Short-lived presigned URL for a call's archived recording. Returns { url } or null. */
export const getCallRecordingUrl = async (websiteId, callId) => {
  const url = `${EntitiesApiUrl}/api/Websites/${websiteId}/support/calls/${callId}/recording`;
  return get(url);
};

/** Outbound SMS traffic + cost for a website: paged rows plus a per-purpose summary. */
export const getSmsLog = async (
  websiteId,
  params?: { from?: string; to?: string; purpose?: string; status?: number; page?: number; pageSize?: number },
) => {
  const url = `${EntitiesApiUrl}/api/Websites/${websiteId}/sms`;
  return get(url, params);
};

/** SMS traffic bucketed by time interval for the insights chart: messages / segments / failed / cost / opens. */
export const getSmsIntervalStats = async (
  websiteId,
  params?: { from?: string; to?: string; interval?: number; purpose?: string },
) => {
  const url = `${EntitiesApiUrl}/api/Websites/${websiteId}/sms/interval-stats`;
  return get(url, params);
};

export const updateWebsiteSupport = async (websiteId, data) => {
  const url = `${EntitiesApiUrl}/api/Websites/${websiteId}/support`;
  return put(url, data);
};

export interface PhoneCredentialCheck {
  ok: boolean;
  provider: string;
  phoneNumber?: string | null;
  /** What failed, in the words of the person who has to fix it. Null when ok. */
  problem?: string | null;
  fix?: string | null;
  credentialsValid: boolean;
  accountSid?: string | null;
  accountFriendlyName?: string | null;
  accountStatus?: string | null;
  /** Whether the account we authenticated as actually owns this number (catches subaccounts). */
  accountOwnsNumber?: boolean | null;
  numberVoiceUrl?: string | null;
  numberVoiceFallbackUrl?: string | null;
  /** Matches the tokenFingerprint in a TwilioSig mismatch log, so config and logs are comparable. */
  tokenFingerprint?: string | null;
  tokenLength: number;
}

/**
 * "Will inbound calls to this number actually work?" — verifies the stored credentials against the
 * provider live, rather than waiting for a customer to discover they don't.
 *
 * Distinguishes the three failures a "signature mismatch" cannot tell apart: a wrong/stale token, a number
 * owned by a different (sub)account, and Twilio calling a different URL than we validate against.
 */
export const testPhoneCredentials = async (websiteId, phoneNumber?: string) => {
  const qs = phoneNumber ? `?phoneNumber=${encodeURIComponent(phoneNumber)}` : '';
  const url = `${EntitiesApiUrl}/api/Websites/${websiteId}/support/test-phone-credentials${qs}`;
  return post(url, {});
};

/** Generate a sample greeting in an ElevenLabs voice and return the ApiResponse ({ data: { url }, message }). */
export const previewSupportVoice = async (websiteId, body: { voiceId: string; apiKey?: string; text?: string }) => {
  const url = `${EntitiesApiUrl}/api/Websites/${websiteId}/support/tts-preview`;
  return post(url, body);
};

/** Short-lived Plivo Browser SDK access token for outbound calling (Stage 1). */
export interface VoipToken {
  token: string;
  identity: string;
  applicationId: string;
  callerId: string;
  expiresInSeconds: number;
  provider?: string; // "plivo" | "twilio" — which browser SDK to load
}

/**
 * Fetch a short-lived outbound VoIP token for the given website.
 * Returns null when outbound calling is not configured (409) or on error.
 */
export async function getVoipToken(websiteId: string): Promise<VoipToken | null> {
  try {
    const url = `${EntitiesApiUrl}/api/phone/voip-token`;
    return await get<VoipToken>(url, { websiteId });
  } catch (error) {
    console.error("Error fetching VoIP token:", error);
    return null;
  }
}

export const getAllWebsites = async () => {
  const url = `${EntitiesApiUrl}/api/Websites`;
  return get(url);
};
// Function to get pending supplier registrations
export async function getPendingSupplierRegistrations(): Promise<WebApiResponse<SupplierRegistration[]> | null> {
  const url = `${EntitiesApiUrl}/api/Account/supplier-registrations/pending`;
  return getResponse(url);
}

export interface SupplierRegistration {
  id: string;
  createdAt: string;
  updatedAt: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  currency: string;
  companyName: string;
  websiteRegistrationInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    websiteName: string;
    websiteUrl: string;
    password: string;
    currency: string;
    unit: number;
    timezoneId: string;
    taxCode: string;
    issuanceDate: string;
    placeOfIssuance: string;
    companyAddress: {
      address: string;
      addressName: string;
      latLng: {
        lat: number;
        lng: number;
      };
    };
  };
}

// Function to accept or reject a supplier registration
export async function processSupplierRegistration(registrationId: string, accept: boolean): Promise<WebApiResponse<any> | null> {
  const url = `${EntitiesApiUrl}/api/Account/supplier-registration/${registrationId}?accept=${accept}`;
  return post(url, {});
}
//#endregion Website API
/* FlightStats API */
export interface FlightArrivalDateRequest {
  flightNumber: string;
  date: string;
  airport: string;
}

export interface IsAirportRequest {
  latLng: {
    lat: number;
    lng: number;
  };
  radiusInMeters: number;
}

interface Delays {
  departureGateDelayMinutes: number;
  arrivalGateDelayMinutes: number;
}

interface FlightStatus {
  flightId: number;
  carrierFsCode: string;
  flightNumber: string;
  departureAirportFsCode: string;
  arrivalAirportFsCode: string;
  departureDate: DateInfo;
  arrivalDate: DateInfo;
  status: string;
  schedule: Schedule;
  operationalTimes: OperationalTimes;
  codeshares: any[];
  delays: Delays;
  flightDurations: FlightDurations;
  airportResources: AirportResources;
  flightEquipment: FlightEquipment;
}

interface DateInfo {
  dateUtc: string;
  dateLocal: string;
}

interface Schedule {
  flightType: string;
  serviceClasses: string;
  restrictions: string;
  uplines: any[];
  downlines: any[];
}

interface OperationalTimes {
  publishedDeparture: DateInfo;
  scheduledGateDeparture: DateInfo;
  estimatedGateDeparture: DateInfo;
  actualGateDeparture: DateInfo;
  estimatedRunwayDeparture: DateInfo;
  actualRunwayDeparture: DateInfo;
  publishedArrival: DateInfo;
  scheduledGateArrival: DateInfo;
  estimatedGateArrival: DateInfo;
  actualGateArrival: DateInfo;
  estimatedRunwayArrival: DateInfo;
  actualRunwayArrival: DateInfo;
}

interface FlightDurations {
  scheduledBlockMinutes: number;
  blockMinutes: number;
  scheduledAirMinutes: number;
  airMinutes: number;
  scheduledTaxiOutMinutes: number;
  taxiOutMinutes: number;
  scheduledTaxiInMinutes: number;
  taxiInMinutes: number;
}

interface AirportResources {
  departureTerminal: string;
  departureGate: string;
  arrivalTerminal: string;
  arrivalGate: string;
}

interface FlightEquipment {
  scheduledEquipmentIataCode: string;
  actualEquipmentIataCode: string;
  tailNumber: string;
  fleetAircraftId: number;
}

interface Appendix {
  airlines: Airline[];
  airports: Airport[];
  equipments: Equipment[];
}

interface Airline {
  fs: string;
  iata: string;
  icao: string;
  name: string;
  active: boolean;
}

interface Airport {
  fs: string;
  iata: string;
  icao: string;
  faa: string;
  name: string;
  city: string;
  cityCode: string;
  countryCode: string;
  countryName: string;
  regionName: string;
  timeZoneRegionName: string;
  weatherZone: string;
  localTime: string;
  utcOffsetHours: number;
  latitude: number;
  longitude: number;
  elevationFeet: number;
  classification: number;
  active: boolean;
  weatherUrl: string;
  delayIndexUrl: string;
}

interface Equipment {
  iata: string;
  name: string;
  turboProp: boolean;
  jet: boolean;
  widebody: boolean;
  regional: boolean;
}

export interface FlightData {
  flightStatuses: FlightStatus[];
  appendix: Appendix;
}

interface Result {
  icao: string;
  iata: string;
  name: string;
  city: string;
  state: string;
  country: string;
  elevation: number;
  lat: number;
  lon: number;
  tz: string;
  waitingTimeInMinutes: number;
}
interface IsAirportResponse {
  result: Result;
  code: number;
  success: boolean;
  errorMessages: string[];
  successMessages: string[];
  errors: string[];
  redirectAfterMs: number;
}

export function getIsAirport(isAirportRequest: IsAirportRequest): Promise<IsAirportResponse> {
  const url = `${CbsApiUrl()}/staticdata/getAirport`;
  return post(url, isAirportRequest);
}

export function getFlightArrivalDate(flightArrivalDateRequest: FlightArrivalDateRequest): Promise<FlightData> {
  return getFlightClient(EntitiesApiUrl).search<FlightData>('status/flight/arrival-date', flightArrivalDateRequest);
}

export function getBookingFlightDetails(bookingGuid: string): Promise<any> {
  const url = `${EntitiesApiUrl}/api/bookings/GetBookingFlightDetails/${bookingGuid}`;
  return get(url);
}
/* END:FlightStats API */

/* Goldsainte API */

export interface CarOwnerRegistratonRequest {
  websiteGuid: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  acceptTermsAndConditions: boolean;
}
export function registerCarOwner(carOwnerRegistrationRequest: CarOwnerRegistratonRequest) {
  const url = `${GoldsainteApiUrl}/account/registerCarOwner`;
  return post(url, carOwnerRegistrationRequest);
}

export function getBookingDetailsForDriverByHash(hash: string) {
  const url = `${GoldsainteApiUrl}/bookings/getDriverBookingInfo`;
  return post(url, { hash: hash });
}

export function addOrUpdateCarOwner(addOrUpdateCarOwnerRequest) {
  const url = `${GoldsainteApiUrl}/carrental/addOrUpdateCarOwner`;
  return post(url, addOrUpdateCarOwnerRequest);
}

export function updateCarRentalAfterBookingUpdate(bookingGuid: string) {
  const url = `${GoldsainteApiUrl}/carrental/updateCarRental/${bookingGuid}`;
  console.log("updateCarRentalAfterBookingUpdate", url);
  return post(url, {});
}

export function getCarOwners() {
  const url = `${GoldsainteApiUrl}/carrental/getCarOwners`;
  return getResponse(url);
}

export function getMyCarOwnerProfile() {
  const url = `${GoldsainteApiUrl}/carrental/getMyCarOwnerProfile`;
  return getResponse(url);
}

export interface UpdateMyCarOwnerProfileRequest {
  id: string;
  paypalId: string;
}
export function updateMyCarOwnerProfile(updateMyCarOwnerProfileRequest: UpdateMyCarOwnerProfileRequest) {
  const url = `${GoldsainteApiUrl}/carrental/updateMyCarOwnerProfile`;
  return post(url, updateMyCarOwnerProfileRequest);
}

export function deleteCarOwner(guid: string) {
  const url = `${GoldsainteApiUrl}/carrental/deleteCarOwner`;
  return post(url, { entityId: guid });
}

export interface CarType {
  make: string;
  model: string;
  imageUrl: string;
  imageId: string;
  description: string;
  isRemoved: boolean;
  id: string;
  createdAt: string;
  updatedAt: string;
}
export function getCarTypes(): Promise<WebApiResponse<CarType[]>> {
  const url = `${GoldsainteApiUrl}/carrental/getCarTypes`;
  return getResponse(url);
}

export function deleteCarType(guid: string): Promise<WebApiResponse<CarType>> {
  const url = `${GoldsainteApiUrl}/carrental/deleteCarType`;
  return post(url, { entityId: guid });
}

export interface AddOrUpdateCarTypeRequest {
  id?: string;
  make: string;
  model: string;
  description: string;
  image: ImageFileInfo | null | undefined;
  //imageUrl: string | null | undefined;
  //imageId: string | null | undefined;
}

export interface ImageFileInfo {
  id: string;
  publicUrl: string;
}
export function addOrUpdateCarType(carTypeToUpdate: AddOrUpdateCarTypeRequest): Promise<WebApiResponse<CarType>> {
  const url = `${GoldsainteApiUrl}/carrental/addOrUpdateCarType`;
  return post(url, carTypeToUpdate);
}

export function getCars() {
  const url = `${GoldsainteApiUrl}/carrental/getCars`;
  return getResponse(url);
}

export function getAvailableCars(getAvailableCarsRequest) {
  const url = `${GoldsainteApiUrl}/carrental/getAvailableCars`;
  return post(url, getAvailableCarsRequest);
}

export function addOrUpdateCar(addOrUpdateCarRequest) {
  const url = `${GoldsainteApiUrl}/carrental/addOrUpdateCar`;
  return post(url, addOrUpdateCarRequest);
}

export function updateCarAvailability(updateCarAvailabilityRequest) {
  const url = `${GoldsainteApiUrl}/carrental/updateCarAvailability`;
  return post(url, updateCarAvailabilityRequest);
}

export function deleteCar(guid: string) {
  const url = `${GoldsainteApiUrl}/carrental/deleteCar`;
  return post(url, { entityId: guid });
}

export interface GetCarRentalsRequest {
  websiteGuid: string;
  carOwnerGuid: string | null;
  numberOfDays: number | null;
  startDate: string | null;
  endDate: string | null;
}

export function getCarRentals(request: GetCarRentalsRequest) {
  const url = `${GoldsainteApiUrl}/carrental/getCarRentals`;
  return post(url, request);
}

export function getCarRentalForBooking(guid: string) {
  const url = `${GoldsainteApiUrl}/carrental/getCarRentalForBooking`;
  return post(url, { entityId: guid });
}
export interface ClientModel {
  guid: string;
  firstName?: string;
  lastName?: string;
  clientGuid?: string;
  companyGuid?: string;
  companyName?: string;
  email: string;
  phone?: string;
}

export function getClientAndCompanyList(websiteId: string): Promise<ApiResponse<ClientModel[]>> {
  const url = `${EntitiesApiUrl}/api/PartnerPayments/${websiteId}/client-company`;
  return getResponse(url);
}
export function createClientPayment(request: CreatePartnerPaymentRequest): Promise<ApiResponse<any>> {
  const url = `${EntitiesApiUrl}/api/PartnerPayments`;
  return post(url, request);
}
export interface AssignCarToCarRentalRequest {
  carRentalId: string;
  carToAssignId: string;
}

export function assignCarToCarRental(request: AssignCarToCarRentalRequest) {
  const url = `${GoldsainteApiUrl}/carrental/assignCarToCarRental`;
  return post(url, request);
}

export interface RejectCarRentalsRequest {
  carRentalId: string;
  message: string | null;
}
export function rejectCarRental(request: RejectCarRentalsRequest) {
  const url = `${GoldsainteApiUrl}/carrental/rejectCarRental`;
  return post(url, request);
}

/* END: Goldsainte API */

// Cars API
export function saveDriverCars(request: SaveCarsRequest) {
  const url = `${CbsApiUrl()}/api/Cars`;
  return post(url, request);
}
export function getDriverCars(id: string): Promise<WebApiResponse<SaveCarsRequest>> {
  const url = `${CbsApiUrl()}/api/Cars/${id}`;
  return get(url);
}
function logout() {
  //store.dispatch({type: 'setUserInfo', data: {}})
  store.dispatch({ type: "setToken", data: null });
  // Force navigation to login — PrivateRoute only re-evaluates on route changes,
  // so clearing the token alone won't redirect the current page.
  window.location.replace("/#/login");
}
export const getImportBookingRules = async (websiteGuid: string): Promise<IImportBookingRule[]> => {
  const url = `${EntitiesApiUrl}/api/import-booking-rules/${websiteGuid}`;
  try {
    const response = await getResponse(url);
    return response.data as IImportBookingRule[];
  } catch (error) {
    console.error("Error fetching import booking rules:", error);
    throw error;
  }
};

export interface UpsertImportBookingRuleRequest {
  id?: string | null;
  websiteGuid: string;
  clientCompanyGuid: string;
  clientType: number; // 1 | 2
  rules: {
    minPassengers: number;
    maxPassengers: number | null;
    vehicleTypeGuid: string;
  }[];
}

export const upsertImportBookingRule = async (payload: UpsertImportBookingRuleRequest): Promise<{ success: boolean; data: any; error: string }> => {
  const url = `${EntitiesApiUrl}/api/import-booking-rules/upsert`;
  try {
    const response = await post(url, payload);
    return response;
  } catch (error) {
    console.error("Error upserting import booking rules:", error);
    throw error;
  }
};
export interface IImportBookingHistoryItem {
  bookingGuid: string;
  customerId: string;
  missingField: string[];
}

export interface IImportBookingHistory {
  createdAt: string;
  websiteGuid: string;
  excelFileUrls: string[];
  bookingHistoryItems: IImportBookingHistoryItem[];
  createdBy: string;
}

export const getImportBookingHistory = async (websiteGuid: string, fromDate: string, toDate: string): Promise<IImportBookingHistory[]> => {
  const url = `${EntitiesApiUrl}/api/bookings/import/history/${websiteGuid}?fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(toDate)}`;
  try {
    const response = await getResponse(url);
    return response.data as IImportBookingHistory[];
  } catch (error) {
    console.error("Error fetching import booking history:", error);
    throw error;
  }
};

export interface ReviewIncidentIncidentTypeCountItem {
  incidentType: string;
  incidentTypeName: string;
  count: number;
}

export interface ReviewIncidentTypeBySourceItem {
  incidentSource: number | string;
  incidentSourceName: string;
  count: number;
  incidentTypes: ReviewIncidentIncidentTypeCountItem[];
}

export type ReviewReportCaseCategory = 'incident' | 'ticket' | 'incidentType';

export interface ReviewReportCase {
  id?: string;
  reviewId?: string;
  incidentId?: string;
  bookingId: string;
  client: string;
  region: string;
  supplierName: string;
  status: string;
  action: string;
  incidentType: string;
  hasProof: boolean;
  proofUrl?: string | null;
}

export interface ReviewReportActionSummary {
  action: string;
  actionName: string;
  category?: ReviewReportCaseCategory;
  currentCount: number;
  previousWeekCount: number;
  previousWeekPercentage?: number;
}

export interface ReviewReportIncidentTypeCases {
  incidentType: string;
  incidentTypeName: string;
  count: number;
  cases: ReviewReportCase[];
}

export interface ReviewReportCaseDetails {
  badReviewsToIncidents: ReviewReportCase[];
  badReviewsToTickets: ReviewReportCase[];
  incidentsByType: ReviewReportIncidentTypeCases[];
}

export interface ReviewIncidentReportResponse {
  negativeReviewsCount: number;
  previousWeekBadReviewsCount: number;
  incidentsWasRaisedFromReviews: number;
  incidentFromBookingDotComCount: number;
  reviewCountBySource: Record<string, number>;
  reviewCountByRegion: Array<{
    region: string;
    count: number;
    reviews: Review[];
  }>;
  incidentsByType: ReviewIncidentTypeBySourceItem[];
  topComplainedSuppliers: Array<{
    supplierWebsiteGuid: string;
    supplierName: string;
    driverGuid: string;
    driverName: string;
    isDriverIncident: boolean;
    incidentCount: number;
    reviews: Review[];
  }>;
  actionSummaries?: ReviewReportActionSummary[];
  caseDetails?: ReviewReportCaseDetails;
  previousWeekCaseDetails?: ReviewReportCaseDetails;
}

export const getReviewIncidentReport = async (
  websiteGuid: string,
  startDate: string | Date,
  endDate: string | Date,
  sources?: string[]
): Promise<ReviewIncidentReportResponse | null> => {
  const params = new URLSearchParams({
    websiteGuid,
    startDate: moment(startDate).toISOString(),
    endDate: moment(endDate).toISOString(),
  });

  if (sources && sources.length > 0) {
    sources
      .map(source => source.trim())
      .filter(Boolean)
      .forEach(source => params.append("sources", source));
  }

  const qs = params.toString();
  return await get<ReviewIncidentReportResponse>(`${EntitiesApiUrl}/api/reports/review-incident?${qs}`);
};

export async function getFromApi(url: string) {
  return getResponse(url);
}

export async function sendFormData(url: string, payload: any) {
  const state = store.getState();
  const config = {
    headers: {
      "Content-Type": "multipart/form-data",
      "Cache-Control": "no-cache",
      Authorization: `Bearer ${state.user?.token}`,
    },
    method: "POST" as Method,
    timeout: 240 * 1000,
  };

  // Accept EITHER a plain object to convert, or an already-built FormData.
  //
  // Object.entries() on a FormData returns [] — its entries live in internal slots, not enumerable
  // properties — so a caller passing FormData used to have every field silently dropped and the server
  // received an empty body ("sessionId is required"). That broke chat image upload for both the operator
  // HelpDesk and the driver page.
  const formData = payload instanceof FormData ? payload : new FormData();
  if (!(payload instanceof FormData)) {
    Object.entries(payload).forEach(([key, value]) => {
      formData.append(key, value as any);
    });
  }

  try {
    const timestamp = new Date().getTime();
    const urlWithTimestamp = url.includes("?") ? `${url}&t=${timestamp}` : `${url}?t=${timestamp}`;
    const response = await axios.post(urlWithTimestamp, formData, config);
    if (response.data?.isUnauthorized) {
      showToastSuccessAndErrorMessagesFromApiResponse(response.data);
      logout();
      return;
    }
    return response.data || response;
  } catch (error) {
    handleError(error);
  }
}

export async function postToApi(url: string, data: any) {
  return post(url, data);
}

export async function putToApi(url: string, data: any) {
  return put(url, data);
}

export type GetResponseOptions = {
  headers?: HeadersInit;
  method?: Method;
  timeoutInSeconds?: number;
};

export async function getResponse(
  url: string,
  options: GetResponseOptions = {}
) {
  const state = store.getState();
  const { timeoutInSeconds = 60, method = "GET", headers = {} } = options;

  // Add timestamp to prevent caching
  const timestamp = new Date().getTime();
  const urlWithTimestamp = url.includes("?") ? `${url}&t=${timestamp}` : `${url}?t=${timestamp}`;

  const config = {
    url: urlWithTimestamp,
    headers: {
      "Content-Type": "application/json",
      "Cache-Control": "no-cache",
      Authorization: `Bearer ${state.user?.token}`,
      ...headers,
    },
    method: method,
    timeout: timeoutInSeconds * 1000,
  };

  try {
    const response = await axios(config);
    return response.data;
  } catch (err) {
    console.error("axios.getResponse error", err);
    throw err;
  }
}

function handleError(error: any) {
  if (error?.response?.status === 401) {
    console.log("Logout user ....");
    logout();
  } else {
    console.error("axios error", error);
    throw error;
  }
}

export async function get<T = any>(url: string, params?: any): Promise<T | null> {
  const state = store.getState();
  const config = {
    headers: {
      "Content-Type": "application/json",
      "Cache-Control": "no-cache",
      Authorization: `Bearer ${state.user?.token}`,
    },
    params,
    method: "GET" as Method,
    timeout: 60 * 1000,
  };

  try {
    const response = await axios.get(url, config);
    return response.data?.result ?? response.data?.data ?? null;
  } catch (err) {
    console.error("axios.get error", err);
    throw err;
  }
}

export async function del(url: string) {
  const state = store.getState();
  const config = {
    headers: {
      "Content-Type": "application/json",
      "Cache-Control": "no-cache",
      Authorization: `Bearer ${state.user?.token}`,
    },
    method: "DELETE" as Method,
    timeout: 60 * 1000,
  };

  try {
    const response = await axios.delete(url, config);
    return {
      success: response.data?.success || false,
      data: response.data || null,
      error: response.data?.error || "Unknown error",
    };
  } catch (err) {
    console.error("axios.delete error", err);
    return { success: false, error: err.message || "Network error" };
  }
}

export async function patch(url: string, data?: any) {
  const state = store.getState();
  const config = {
    headers: {
      "Content-Type": "application/json",
      "Cache-Control": "no-cache",
      Authorization: `Bearer ${state.user?.token}`,
    },
    method: "PATCH" as Method,
    timeout: 120 * 1000,
    responseType: "json" as ResponseType,
  };

  try {
    const response = await axios.patch(url, data, config);
    if (response.data?.isUnauthorized) {
      showToastSuccessAndErrorMessagesFromApiResponse(response.data);
      logout();
      return;
    }
    return response.data || response;
  } catch (error) {
    handleError(error);
  }
}

export async function getBlobResponse(url: string) {
  const state = store.getState();
  const config = {
    headers: {
      "Content-Type": "application/json",
      "Cache-Control": "no-cache",
      Authorization: `Bearer ${state.user?.token}`,
    },
    method: "GET" as Method,
    timeout: 60 * 1000,
    responseType: "blob" as ResponseType,
  };

  try {
    const response = await axios.get(url, config);
    return response.data;
  } catch (err) {
    console.error("axios.getBlobResponse error", err);
    throw err;
  }
}

export async function post(url: string, data?: any) {
  const state = store.getState();
  const config = {
    headers: {
      "Content-Type": "application/json",
      "Cache-Control": "no-cache",
      Authorization: `Bearer ${state.user?.token}`,
    },
    method: "post" as Method,
    timeout: 120 * 1000,
    responseType: data?.responseType || ("json" as ResponseType),
  };

  try {
    const timestamp = new Date().getTime();
    const urlWithTimestamp = url.includes("?") ? `${url}&t=${timestamp}` : `${url}?t=${timestamp}`;
    const response = await axios.post(urlWithTimestamp, data, config);
    if (response.data?.isUnauthorized) {
      showToastSuccessAndErrorMessagesFromApiResponse(response.data);
      logout();
      return;
    }
    return response.data || response;
  } catch (error) {
    handleError(error);
  }
}

export async function put(url: string, data: any) {
  const state = store.getState();
  const config = {
    headers: {
      "Content-Type": "application/json",
      "Cache-Control": "no-cache",
      Authorization: `Bearer ${state.user?.token}`,
    },
    method: "PUT" as Method,
    timeout: 90 * 1000,
  };

  try {
    const response = await axios.put(url, data, config);
    if (response.data?.isUnauthorized) {
      showToastSuccessAndErrorMessagesFromApiResponse(response.data);
      logout();
      return;
    }
    return response.data || response;
  } catch (error) {
    handleError(error);
  }
}

function getPostOptions(body: any): RequestInit {
  const state = store.getState();
  const headers = new Headers({
    "Content-Type": "application/json",
    "Cache-Control": "no-cache",
  });

  if (state.user?.token) {
    headers.append("Authorization", `Bearer ${state.user.token}`);
  }

  return {
    method: "POST" as Method,
    headers,
    body,
    redirect: "follow",
  };
}

function getGetOptions(): RequestInit {
  const state = store.getState();
  const headers = new Headers({
    accept: "*/*",
    "Cache-Control": "no-cache",
  });

  if (state.user?.token) {
    headers.append("Authorization", `Bearer ${state.user.token}`);
  }

  return {
    method: "GET" as Method,
    headers,
  };
}

/* Type definitions */

export interface MessageInfo {
  /**
   * If the error is linked to a field in form -> this will contain the field name. Otherwise null
   */
  fieldName: string | null;
  /**
   * Error code used for client internationalization
   */
  code: number;
  /**
   * Message in English
   */
  message: string;
}

export interface WebApiResponse<T> {
  code: number;
  title: string;
  successMessages: MessageInfo[];
  errorMessages: MessageInfo[];
  success: boolean;
  data: T;
}

export type SimpleApiResponse = {
  code: number;
  title: string;
  successMessages: MessageInfo[];
  errorMessages: MessageInfo[];
  success: boolean;
};

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  result: T;   //this is the obsolete one, kept for backward compatibility
  errors: string[];
  errorMessages: ErrorMessage[];
  statusCode: number;
  code?: number;
  successMessages?: string[];
  isUnauthorized?: boolean;
}

export interface ErrorMessage {
  code: number;
  message: string;
}

export interface LoginResult {
  guid: string;
  websiteGuid: string;
  preferredRegion: string;
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  jwtToken: string;
  websites: WebsiteInfo[];
  dateFormat: string;
  timeFormat: string;
  isSuperAdmin?: boolean | undefined;
}

export interface WebsiteInfo {
  isDefault: boolean;
  websiteGuid: string;
  websiteName: string;
  roles: string[];
  allowedRegionConfigs?: AllowedRegionConfig[];
}

export interface IntervalStatsInfo {
  regions: string[];
  statistics: IntervalStats[];
}

export interface IntervalStats {
  fromDate: string;
  toDate: string;
  intervalInMinutes: number;
  requestsCount: number;
  priceGivenCount: number;
  completedBookingsCount: number;
  pricePlanGuid: string;
  pricePlanName: string;
}

export interface RegionInfo {
  name: string;
  guid: string;
}
export interface BookingDotComPricePlan {
  name: string;
  guid: string;
  radiusInkilometers: number;
}

export interface AddNewSupplierRequest {
  websiteGuid: string;
  supplierEmail: string;
}

export interface InviteSupplierRequest {
  websiteGuid: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
}

export interface CreateClientRequest {
  supplierGuid: string;
}

export interface UpdateSupplierRequest {
  guid: string;
  name: string;
  currency: string;
  inheritWebsiteBanking?: boolean;
  passClientEmail: boolean;
  passClientPhone: boolean;
  serviceMappings: SupplierServiceMappingsInfo[];
  coveredRegionsIds: string[];
  bankName?: string;
  ibanAccountNumber?: string;
  swiftCode?: string;
  bankAddress?: string;
  routingNumber?: string;
  accountNumber?: string;
  sortCode?: string;
}

export interface ServiceMappingIds {
  providerServiceGuid: string;
  supplierServiceGuid: string;
}

export interface UpdateSupplierServicesRequest {
  providerWebsiteGuid: string;
  supplierWebsiteGuid: string;
  serviceMappings: ServiceMappingIds[];
}

export interface SupplierInfo {
  guid: string;
  name: string;
  websiteGuid: string;
  clientGuid: string;
  supplierWebsiteGuid: string;
  services: SupplierServiceInfo[];
  serviceMappings: SupplierServiceMappingsInfo[];
  inheritWebsiteBanking?: boolean;
  passClientPhone: boolean;
  passClientEmail: boolean;
  currency: string;
  supplierWebsiteName?: string;
  websiteBankName?: string;
  websiteIbanAccountNumber?: string;
  websiteSwiftCode?: string;
  websiteBankAddress?: string;
  websiteRoutingNumber?: string;
  websiteAccountNumber?: string;
  websiteSortCode?: string;
  bankName?: string;
  ibanAccountNumber?: string;
  swiftCode?: string;
  bankAddress?: string;
  routingNumber?: string;
  accountNumber?: string;
  sortCode?: string;
  providerServices: ProviderServiceInfo[];
  clientName: string;
  clientEmail: string;
  clientPhone?: string;
  invitationId: InvitationIdInfo;
  coveredRegions: CoveredRegionInfo[];
  // Legacy properties for backward compatibility
  createdAt?: Date;
  updatedAt?: Date;
}

export interface SupplierOrDriverInfo {
  guid: string;
  fullName: string;
  email: string;
  phone: string;
  type: "Supplier" | "Driver";
}

export interface Coupon {
  isEnabled: boolean;
  code: string;
  startDate: Date;
  endDate: Date;
  appliestoFixedPrices: boolean;
  discountPercentage: number;
  discounValue: number;
  currency: string;
  pricePlan: string[];
  clients: string[];
}

export interface ClientPriceSource {
  clientGuid: string;
  markupPercentage: number;
  /** 0 = all prices; 1 = only regions without the client's own prices. */
  mode: 0 | 1;
}

export interface Client {
  priceSource?: ClientPriceSource | null;
  firstName: string;
  lastName: string;
  phone: string | null;
  discountPercentage: number | null;
  useCompanyForInvoice: boolean;
  userId: number;
  clientId: string;
  isRemoved: boolean;
  salutation: string | null;
  currency: string | null;
  creditCardSetting: number;
  overwriteDriverAssigned: boolean;
  automaticallySendDriverInfoToCustomerByEmail: boolean;
  automaticallySendDriverInfoToCustomerBySms: boolean;
  overwriteDriverArrived: boolean;
  automaticallySendDriverArrivedEmail: boolean;
  automaticallySendDriverArrivedSms: boolean;
  showInstantPriceWhenBooking: boolean;
  hidePriceFromCustomer: boolean;
  companyName: string;
  streetAddress: any;
  additionalAddress: any;
  zipCode: any;
  city: any;
  country: any;
  costCenter: any;
  vatNumber: any;
  allowApiAccess: boolean;
  clientSecret: string | null;
}

export interface TravelAgentRegistrationRequest {
  websiteGuid: string; // Guid as a string

  email: string;
  firstName: string;
  lastName: string;
  phone: string;

  agencyName: string;
  consortiumName: string;

  streetAddress: string;
  additionalAddress: string;
  zipCode: string;
  city: string;
  country: string;

  referralCode: string;
}

export interface CompleteTravelAgentRegistrationRequest {
  clientId: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export interface SupplierServiceMappingsInfo {
  providerServiceName: string;
  supplierServiceName: string;
  supplierServiceEquivalent: string;
  supplierNumberOfPassenger: number;
  supplierNumberOfLuggages: number;
  providerServiceGuid: string;
  supplierServiceGuid: string;
}

export type AllowedCarTypes = "SEDAN" | "SUV" | "VAN" | "BUS";
export type AllowedCarPriceCategories = "ECONOMY" | "PREMIUM" | "FIRST";

export interface SupplierServiceInfo {
  serviceGuid: string;
  serviceName: string;
  serviceEquivalent: string;
  priceCategory: AllowedCarPriceCategories;
  carType: AllowedCarTypes;
  numberOfPassenger: number;
  numberOfLuggages: number;
  isElectric?: boolean;
}

export interface ProviderServiceInfo {
  serviceGuid: string;
  serviceName: string;
  serviceEquivalent: string;
  priceCategory: string;
  carType?: string;
  numberOfPassenger: number;
  numberOfLuggages: number;
  isElectric?: boolean;
}

export interface CoveredRegionInfo {
  id: string;
  name: string;
}

export interface InvitationIdInfo {
  timestamp: number;
  machine: number;
  pid: number;
  increment: number;
  creationTime: string;
}

export interface ClientInfo {
  priceSource?: ClientPriceSource | null;
  firstName: string;
  lastName: string;
  email: string;
  userGuid: string;
  websiteGuid: string;
}

export interface DriverInfo {
  guid: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export interface SendSmsRequest {
  bookingGuid: string;
  toNumbers: string[];
  message: string;
}

export interface AssignDriverRequest {
  bookingGuid: string;
  driverGuid: string;
  payToDriver: number;
  carId: string;
  sendConfirmationEmailToDriver: boolean;
  sendConfirmationEmailToClient: boolean;
  sendConfirmationSmsToDriver: boolean;
}

export interface PerformanceInfo {
  currency: string;
  bookingsCount: number;
  totalEarnings: number;
  totalPayments: number;
  payments: { [key: string]: number };
  profit: number;
  losses: BookingProfitInfo[];
}

export interface BookingProfitInfo {
  customerId: string;
  currency: string;
  profit: number;
}

export interface SetBookingDriverStatusPayload {
  guid: string;
  driverAcceptanceStatus: DriverAcceptanceStatus;
  driverOnRouteStatus: DriverOnRouteStatus;
}

export enum DriverAcceptanceStatus {
  NotSet = 0,
  Pending = 1,
  Declined = 2,
  Accepted = 3,
}

export enum DriverOnRouteStatus {
  NotSet = 0,
  NotStarted = 1,
  OnMyWay = 2,
  WaitingForPassenger = 3,
  PassengerOnBoad = 4,
  DroppedPassenger = 5,
  PassengerNoShow = 6,
  MissedBooking = 7,
}

export interface BookingCancellationRequest {
  hash: string;
  reason: string;
  reasonDescription: string;
  sendConfirmationEmailToDriver: boolean;
  sendConfirmationSmsToDriver: boolean;
  sendConfirmationEmailToClient: boolean;
  clientReimbursementValue: number;
  driverPaymentValue: number;
}
export enum PaymentType {
  Driver = 1,
  Supplier = 2,
  Client = 3
}

export interface BookingPaymentInfo {
  bookingGuid: string;
  amount: number;
  from: string;
  to: string;
  pickupDateTime: string;
  customerId: string;
}

export interface CreatePartnerPaymentRequest {
  websiteGuid: string;
  guid: string;
  clientId?: string;
  companyId?: string;
  partnerGuid?: string;
  type: PaymentType;
  paymentDate: string;
  paymentMethod: string;
  bookings: BookingPaymentInfo[];
}
// export interface SaveDriverRequest {
//    additionalInfo?: string;
//    additionalInfoDispatcher?: string;
//    primaryAddress: Address;
//    knownLanguages: Array<Language>;
//    dateOfBirth: string;
//    timezone: string;
//    nationality: string;
//    gender: string;
//    currency: string;
//    paymentMethod: string;
//    paymentPeriod: string;
//    websiteGuid: string;
//    guid: string;
//    userGuid: string;
//    email: string;
//    firstName: string;
//    lastName: string;
//    phoneNumber: string;
//    isHired: boolean;
//    driverImage?: ImageInfo
//    driverLicence?: DriverLicence;
//    driverPcoLicence?: Document;
//    driverInsurance?: Document;
//    driverPassport?: Document;
//    carInsurance?: Document;
//    isAdmin: boolean;
//    devices: Array<Device>;
//  }

interface ImageInfo {
  id: string;
  name: string;
  key: string;
  isTemporary: boolean;
  publicUrl: string;
  contentType: string;
  type: string;
}
interface Document {
  guid: string;
  documentNumber: string;
  expirationDate: string;
  image: ImageInfo;
  isMandatory: boolean;
}
interface DriverLicence {
  guid: string;
  documentNumber: string;
  expirationDate: string;
  frontImage: ImageInfo;
  backImage: ImageInfo;
  isMandatory: boolean;
}
interface Device {
  guid: string;
  deviceName?: string;
  deviceYearClass?: string;
  instalationId: string;
  osVersion: string;
  osName: string;
  appName: string;
  buildNumber: string;
  areNotificationsEnabled: boolean;
  isLocationEnabled: boolean;
  isLocationAlwaysEnabled: boolean;
  locationStatus: string;
  notificationsStatus: string;
}
interface Location {
  lat: number;
  lng: number;
}
interface Language {
  name: string;
  level: number;
}
interface Address {
  name: string;
  addressName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  latLng: Location;
}

interface ImageInfo {
  id: string;
  publicUrl: string;
  type: string;
}
interface SaveVehicleRequest {
  id: string;
  createdAt: string;
  updatedAt: string;
  isRemoved: boolean;
  websiteGuid: string;
  carOwnerId: string;
  carOwnerType: number;
  make: string;
  model: string;
  year: number;
  color: string;
  plateNumber: string;
  images: ImageInfo[];
  documents: DocumentInfo[];
}
interface DocumentInfo extends ImageInfo {
  expiration: string;
}
interface SaveCarsRequest {
  id: string;
  createdAt: string;
  updatedAt: string;
  isRemoved: boolean;
  websiteGuid: string;
  carOwnerId: string;
  carOwnerType: number;
  make: string;
  model: string;
  year: number;
  color: string;
  plateNumber: string;
  images: ImageInfo[];
  documents: DocumentInfo[];
}

interface ImageInfo {
  id: string;
  publicUrl: string;
  type: string;
}

interface DocumentInfo {
  id: string;
  publicUrl: string;
  type: string;
  expiration: string;
}

export interface DriverLocation {
  id: string;
  guid: string;
  userGuid: string;
  websiteGuid: string;
  email: string;
  phone: string;
  whatsApp: string;
  activeBookingGuid: string;
  updatedAt: string;
  dbLocationUpdatedAt: string;
  firstName: string;
  lastName: string;
  fullName: string;
  lastLocation: LastLocation;
}

export interface LastLocation {
  timestamp: string;
  driverGuid: string;
  bookingGuid: string;
  driverAppVersion: string;
  accuracy: number;
  altitude: number;
  altitudeAccuracy: number;
  heading: number;
  speed: number;
  lat: number;
  lng: number;
  activeBookingGuid: string;
  activeBookingClientId: string;
  activeBookingPickupUtc: string;
  activeBookingTimezoneId: string;
  localPickupDateTime: string;
}

export interface WebsiteAdministrator {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  allowedRegionConfigs?: AllowedRegionConfig[];
  clientAccess?: ClientAccess | null;
}

/** All = every client, Selected = only clientIds/companyIds, None = direct bookings only (no client picker). */
export type ClientAccessMode = "All" | "Selected" | "None";

/** Which clients a non-admin may see and pick on a website. Missing = All. */
export interface ClientAccess {
  mode: ClientAccessMode;
  clientIds: string[];
  companyIds: string[];
}

export interface AllowedRegionConfig {
  pricePlanGuid: string;
  regionName: string;
  canSeePrice: boolean;
  role?: string;
}
export interface ReviewsRequest {
  SearchText?: string;
  FromDate?: string;
  ToDate?: string;
  MaxOverall?: number;
  HasFeedback?: boolean;
  Status?: number;
  Source?: string;
  Sources?: string[];
  CompanyId?: string;
  PageNumber: number;
  PageSize: number;
  SortBy: string;
  SortOrder: string;
}

export interface ReviewsExportRequest {
  SearchText?: string;
  FromDate?: string;
  ToDate?: string;
  MinOverall?: number;
  MaxOverall?: number;
  HasFeedback?: boolean;
  Status?: number;
  Source?: string;
  Sources?: string[];
  SortBy?: string;
  SortOrder?: string;
}

export interface LocalizableText {
  translations: { [key: string]: string };
  value: string;
  defaultLanguage: string;
}


export interface ReviewsResponse {
  totalCount: number;
  currentCount: number;
  remainingCount: number;
  totalPages: number;
  currentPage: number;
  remainingPages: number;
  pageSize: number;
  data: Review[];
}

export interface Review {
  bookingGuid: string;
  websiteId: string;
  customerId: string;
  ianaTimeZoneId: string;
  driverInfo: {
    driverId: string;
    driverName: string;
    supplierId: string;
    supplierName: string;
  }
  journeyDetails: {
    pickupLocationAddress: string;
    pickupLocationName: string;
    destinationAddress: string;
    destinationName: string;
  };
  vehicleInfo: {
    vehicleId: string;
    vehicleType: string;
  };
  ratings: {
    overall: number;
    driver: number;
    vehicle: number;
    bookingProcess: number;
    valueForMoney: number;
  };
  questionResponses: any[];
  feedback: {
    positiveComment: LocalizableText;
    areasForImprovement: LocalizableText;
    negativeComment: LocalizableText;
    type: number;
  };
  region: string[];
  isAcceptIncidentRaising?: boolean;
  tags: string[];
  isPublic: boolean;
  status: number;
  dispatcherComment?: string;
  id: string;
  incidentId?: string;
  createdAt: string;
  source: string;
  updatedAt: string;
}

export function getReviews(
  websiteGuid: string,
  request: ReviewsRequest
): Promise<ApiResponse<ReviewsResponse>> {
  const params = new URLSearchParams({
    PageNumber: request.PageNumber.toString(),
    PageSize: request.PageSize.toString(),
    SortBy: request.SortBy,
    SortOrder: request.SortOrder,
  });

  // Only add parameters if they have values
  if (request.SearchText && request.SearchText.trim()) {
    params.append("SearchText", request.SearchText.trim());
  }

  if (request.FromDate) {
    params.append("FromDate", request.FromDate);
  }

  if (request.ToDate) {
    params.append("ToDate", request.ToDate);
  }

  if (request.MaxOverall != null) {
    params.append("MaxOverall", request.MaxOverall.toString());
  }

  if (request.HasFeedback != null) {
    params.append("HasFeedback", request.HasFeedback.toString());
  }

  if (request.Status != null) {
    params.append("Status", request.Status.toString());
  }
  if (request.Sources && request.Sources.length > 0) {
    request.Sources
      .map(source => source.trim())
      .filter(Boolean)
      .forEach(source => params.append("Sources", source));
  } else if (request.Source && request.Source.trim()) {
    params.append("Source", request.Source.trim());
  }

  if (request.CompanyId && request.CompanyId.trim()) {
    params.append("companyId", request.CompanyId.trim());
  }

  const url = `${EntitiesApiUrl}/api/reviews?websiteId=${websiteGuid}&${params.toString()}`;
  return getResponse(url);
}

export async function exportReviews(
  websiteGuid: string,
  request: ReviewsExportRequest
): Promise<{ blob: Blob; fileName: string }> {
  const state = store.getState();
  const params = new URLSearchParams({
    websiteId: websiteGuid,
  });

  if (request.SearchText && request.SearchText.trim()) {
    params.append("searchText", request.SearchText.trim());
  }

  if (request.FromDate) {
    params.append("fromDate", request.FromDate);
  }

  if (request.ToDate) {
    params.append("toDate", request.ToDate);
  }

  if (request.MinOverall != null) {
    params.append("minOverall", request.MinOverall.toString());
  }

  if (request.MaxOverall != null) {
    params.append("maxOverall", request.MaxOverall.toString());
  }

  if (request.HasFeedback != null) {
    params.append("hasFeedback", request.HasFeedback.toString());
  }

  if (request.Status != null) {
    params.append("status", request.Status.toString());
  }

  if (request.Sources && request.Sources.length > 0) {
    request.Sources
      .map(source => source.trim())
      .filter(Boolean)
      .forEach(source => params.append("sources", source));
  } else if (request.Source && request.Source.trim()) {
    params.append("source", request.Source.trim());
  }

  if (request.SortBy) {
    params.append("sortBy", request.SortBy);
  }

  if (request.SortOrder) {
    params.append("sortOrder", request.SortOrder);
  }

  const url = `${EntitiesApiUrl}/api/reviews/export?${params.toString()}`;
  const response = await axios.get(url, {
    headers: {
      "Content-Type": "application/json",
      "Cache-Control": "no-cache",
      Authorization: `Bearer ${state.user?.token}`,
    },
    responseType: "blob" as ResponseType,
    timeout: 120 * 1000,
  });

  const dispositionHeader = response.headers["content-disposition"] || response.headers["Content-Disposition"];
  const fileNameMatch = dispositionHeader?.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
  const fileName = fileNameMatch?.[1]?.replace(/"/g, "") || "reviews.xlsx";

  return {
    blob: response.data,
    fileName: decodeURIComponent(fileName),
  };
}

export interface CommentReviewRequest {
  dispatcherComment: string;
  isAcceptIncidentRaising: boolean;
}

export function commentReview(
  reviewId: string,
  request: CommentReviewRequest
): Promise<ApiResponse<Review>> {
  const url = `${EntitiesApiUrl}/api/reviews/${reviewId}/comment`;
  return put(url, request);
}

export function getReviewById(
  reviewId: string
): Promise<ApiResponse<Review>> {
  const url = `${EntitiesApiUrl}/api/reviews/${reviewId}`;
  return getResponse(url);
}

export interface CreateReviewRequest {
  bookingId: string;
  ratings: {
    overall: number;
    driver: number;
    vehicle: number;
    bookingProcess: number;
    valueForMoney: number;
  };
  feedback: {
    positiveComment?: string;
    areasForImprovement?: string;
  };
  source?: string;
}

export function createReview(
  request: CreateReviewRequest
): Promise<ApiResponse<Review>> {
  const url = `${EntitiesApiUrl}/api/reviews`;
  return post(url, request);
}

export function updateReviewStatus(
  reviewId: string,
  status: number
): Promise<ApiResponse<Review>> {
  const url = `${EntitiesApiUrl}/api/reviews/${reviewId}/status`;
  const params = new URLSearchParams();
  params.append("Status", status.toString());
  return put(`${url}?${params.toString()}`);
}

export function updateReviewFeedbackType(
  reviewId: string,
  type: number
): Promise<ApiResponse<Review>> {
  const url = `${EntitiesApiUrl}/api/reviews/${reviewId}/feedback-type`;
  const params = new URLSearchParams();
  params.append("type", type.toString());
  return put(`${url}?${params.toString()}`);
}

export interface BulkUpdateReviewStatusRequest {
  reviewIds: string[];
  status: number;
}

export function updateReviewsStatusBulk(
  request: BulkUpdateReviewStatusRequest
): Promise<ApiResponse<Review[]>> {
  const url = `${EntitiesApiUrl}/api/reviews/bulk/status`;
  return put(url, request);
}

interface PickupImage {
  id?: string;
  imageUrl: string;
  description: string;
  altText: string;
}

export interface PickupInstructionsItem {
  supplierDriverGuid: string | null;
  name: string;
  websiteGuid: string;
  airportIATA: string | null;
  description: string;
  instructionText?: string;
  meetingPoints?: PickupMeetingPoint[];
  pickupPointGroup?: string;
  pickupPointLabel?: string;
  pickupImages: PickupImage[];
  ownerType?: PickupInstructionsOwnerType;
  ownerGuid?: string | null;
  language?: string;
  steps?: string[];
  boundary?: LocationBoundary | null;
  id: string;
  createdAt: string;
  updatedAt: string;
  isRemoved: boolean;
}

export type UpdateBookingPaymentStatusRequest = {
  bookingGuid: string;
  newStatus: number;
};

export type UpdateBookingPaymentStatusResponse = {
  bookingGuid: string;
  bookingId: number;
  paymentStatus: number;
  paymentMethod: string;
  updatedAt: string;
};
export interface IImportBookingRule {
  websiteGuid: string;
  clientCompanyGuid: string;
  clientType: number;
  rules: IVehicleMappingRule[];
}

export interface IVehicleMappingRule {
  minPassengers: number;
  maxPassengers: number | null;
  vehicleTypeGuid: string;
}
export async function updateBookingPaymentStatus(
  request: UpdateBookingPaymentStatusRequest
): Promise<ApiResponse<UpdateBookingPaymentStatusResponse>> {
  const url = `${CbsApiUrl()}/api/bookings/updatePaymentStatus`;
  return post(url, request);
}
